import {
  BookOpenCheck,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  FileStack,
  ExternalLink,
  Eye,
  EyeOff,
  Info,
  Lock,
  LoaderCircle,
  LogOut,
  Mail,
  Megaphone,
  MessageCircle,
  PlusCircle,
  RefreshCcw,
  Search,
  ShieldAlert,
  Trash2,
  UploadCloud,
  UserCog,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdminLaunchChecklistPanel } from "@/components/admin/AdminLaunchChecklistPanel";
import { AdminAnalyticsExpansionPanel } from "@/components/admin/AdminAnalyticsExpansionPanel";
import { AdminBillingTelemetryPanel } from "@/components/admin/AdminBillingTelemetryPanel";
import { AgentNetworkWorkspace } from "@/components/admin/AgentNetworkWorkspace";
import { MarketingStudioWorkspace } from "@/components/admin/MarketingStudioWorkspace";
import { AdminMarketInsightsPanel } from "@/components/admin/AdminMarketInsightsPanel";
import { AdminReferralPartnerLedger } from "@/components/admin/AdminReferralPartnerLedger";
import { SovereignListingStudio } from "@/components/admin/SovereignListingStudio";
import { AmbassadorAlphaCockpit } from "@/components/admin/AmbassadorAlphaCockpit";
import { AmbassadorClaimVerifyModal } from "@/components/admin/AmbassadorClaimVerifyModal";
import { AmbassadorComplianceBar } from "@/components/admin/AmbassadorComplianceBar";
import { AmbassadorFoundingHud } from "@/components/admin/AmbassadorFoundingHud";
import { AmbassadorLeadCommandCenter } from "@/components/admin/AmbassadorLeadCommandCenter";
import { AmbassadorRolloutMonitor } from "@/components/admin/AmbassadorRolloutMonitor";
import { AmbassadorSeatLedger } from "@/components/admin/AmbassadorSeatLedger";
import { ActivationAuditTable } from "@/components/admin/ActivationAuditTable";
import { AgentDashboardSkeleton } from "@/components/agent/AgentPortalSkeletons";
import { AgentProfileSettingsSheet } from "@/components/agent/AgentProfileSettingsSheet";
import { AdminUserDrawer } from "@/components/admin/AdminUserDrawer";
import { AdminUsersCommandCenter } from "@/components/admin/AdminUsersCommandCenter";
import { AdminVirtualStagingPanel } from "@/components/admin/AdminVirtualStagingPanel";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { FoundingMemberCelebration } from "@/components/admin/FoundingMemberCelebration";
import { ListingMediaManager } from "@/components/admin/ListingMediaManager";
import { SignatureWelcomeModal } from "@/components/SignatureWelcomeModal";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSeo } from "@/hooks/useSeo";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { getBillingPackage, getPackageIdForFeature, getPackageIdForPlanType } from "@/lib/billing";
import { getInvestmentBadgePresentation } from "@/lib/investmentBadge";
import { PLAN_OPTIONS, SUBSCRIPTION_STATUS_OPTIONS, getUpgradeCopy, hasPlanFeature, isReadOnlySubscription } from "@/lib/planEntitlements";
import { formatCategoryLabel, formatDate, formatListingPrice } from "@/lib/portalHelpers";

const POLLING_INTERVAL = 12000;
const USER_ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
  { value: "agent", label: "Agent" },
];
const PLAN_TYPE_OPTIONS = PLAN_OPTIONS;
const PENDING_WELCOME_SIGNATURE_KEY = "pending-signature-welcome";
const CLAIM_VERIFY_MODAL_KEY_PREFIX = "ambassador-claim-verify-dismissed";

const getPendingWelcomeSignatureFlag = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("welcome_signature") === "1" || window.sessionStorage.getItem(PENDING_WELCOME_SIGNATURE_KEY) === "1";
};

const makeNewUserForm = () => ({
  name: "",
  email: "",
  password: "",
  role: "agent",
  phone: "",
  cea_registration_number: "",
  subdomain_slug: "",
  profile_photo_url: "",
  team_name: "",
  team_logo_url: "",
  subscription_status: "Trial",
  plan_type: "Digital Agent",
});

const makeUserDraft = (member) => ({
  name: member.name,
  role: member.role,
  phone: member.phone || "",
  cea_registration_number: member.cea_registration_number || "",
  subdomain_slug: member.subdomain_slug || "",
  profile_photo_url: member.profile_photo_url || "",
  team_name: member.team_name || "",
  team_logo_url: member.team_logo_url || "",
  subscription_status: member.subscription_status || "Trial",
  plan_type: member.plan_type || "Digital Agent",
});

const makeNetworkDealForm = () => ({
  lead_type: "Residential",
  transaction_type: "Buy",
  budget: "",
  location_district: "",
  referral_fee_percentage: "20",
  description: "",
});

const makeMarketingCampaignTemplate = () => ({
  id: "",
  creator_id: "",
  listing_id: "",
  listing_title: "",
  listing_owner_id: "",
  publishing_target: "Agent_Personal",
  campaign_status: "Draft",
  scheduled_for: null,
  content_payloads: {
    facebook_instagram: { text: "", image_url: "", status: "Draft" },
    linkedin: { text: "", image_url: "", status: "Draft" },
    video_reels: { text: "", video_url: "", image_urls: [], asset_urls: [], status: "Draft" },
  },
  publishing_result: {
    mocked: true,
    provider: "MOCKED_AYRSHARE",
    status: "idle",
    message: "Connect at least one social account to activate the publishing queue.",
    job_id: null,
    connected_platforms: [],
    published_platforms: [],
    missing_platforms: ["linkedin", "facebook", "instagram", "x"],
    scheduled_for: null,
    last_attempt_at: null,
    published_at: null,
  },
  video_render_result: {
    provider: "CREATOMATE_READY",
    status: "idle",
    message: "Short-form video automation is ready when this campaign is approved.",
    job_id: null,
    asset_urls: [],
    source_image_urls: [],
    price_label: null,
    script: null,
    request_payload: {},
    completed_at: null,
  },
  created_at: "",
  updated_at: "",
});

const SOCIAL_PLATFORM_ORDER = ["linkedin", "facebook", "instagram", "x"];

const makeMarketingSocialAccountDrafts = () => ({
  linkedin: "",
  facebook: "",
  instagram: "",
  x: "",
});

const fromMarketingInputValue = (inputValue) => {
  if (!inputValue) return null;
  const parsed = new Date(inputValue);
  if (Number.isNaN(parsed.getTime())) return inputValue;
  return parsed.toISOString();
};

const makeListingForm = () => ({
  title: "",
  category: "buy",
  district: "",
  tenure: "Freehold",
  property_type: "Condo",
  price: 1800000,
  sqft: 980,
  bedrooms: 3,
  bathrooms: 2,
  address: "",
  description: "",
  status: "Available",
  featured: false,
  hidden: false,
  image_url: "",
  brochure_url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
  tour_embed_url: "",
  staging_before_url: "",
  staging_after_url: "",
  empty_image_url: "",
  staged_image_url: "",
  gallery_urls: "",
  floorplan_urls: "",
  floorplan_pdf_urls: [],
  siteplan_urls: "",
  siteplan_pdf_urls: [],
  tags: "",
  completion_year: 2026,
  admin_media: { primary_image: {}, gallery: [] },
});

const makePrivateTransactionForm = (user, isAgentView) => ({
  source_name: isAgentView ? `${user.name || "Agent"} Private Transaction Desk` : "Private Transaction Desk",
  notes: "",
  owner_id: isAgentView ? user.id : "",
});

const toCommaList = (values = []) => values.join(", ");

const mapListingToForm = (listing) => ({
  ...makeListingForm(),
  ...listing,
  title: listing.title || "",
  district: listing.district || "",
  tenure: listing.tenure || "Freehold",
  property_type: listing.property_type || "Condo",
  address: listing.address || "",
  description: listing.description || "",
  status: listing.status || "Available",
  image_url: listing.image_url || "",
  brochure_url: listing.brochure_url || "",
  tour_embed_url: listing.tour_embed_url || "",
  staging_before_url: listing.staging_before_url || listing.empty_image_url || "",
  staging_after_url: listing.staging_after_url || listing.staged_image_url || "",
  empty_image_url: listing.empty_image_url || "",
  staged_image_url: listing.staged_image_url || "",
  gallery_urls: toCommaList(listing.gallery_urls || []),
  floorplan_urls: toCommaList(listing.floorplan_urls || []),
  floorplan_pdf_urls: listing.floorplan_pdf_urls || [],
  siteplan_urls: toCommaList(listing.siteplan_urls || []),
  siteplan_pdf_urls: listing.siteplan_pdf_urls || [],
  tags: toCommaList(listing.tags || []),
  admin_media: listing.admin_media || { primary_image: {}, gallery: [] },
});

const splitCommaList = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const makePayload = (form) => ({
  ...form,
  price: Number(form.price),
  sqft: Number(form.sqft),
  bedrooms: Number(form.bedrooms),
  bathrooms: Number(form.bathrooms),
  completion_year: Number(form.completion_year),
  gallery_urls: splitCommaList(form.gallery_urls),
  floorplan_urls: splitCommaList(form.floorplan_urls),
  floorplan_pdf_urls: form.floorplan_pdf_urls || [],
  siteplan_urls: splitCommaList(form.siteplan_urls),
  siteplan_pdf_urls: form.siteplan_pdf_urls || [],
  tags: splitCommaList(form.tags),
});

const optimizeCloudinaryUrl = (url, width = 1800) => {
  if (!url?.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/f_auto,q_auto,c_limit,w_${width}/`);
};

const getAssetDisplayLabel = (url = "") => {
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split("/").pop() || url);
  } catch {
    return url;
  }
};

const moveGalleryAsset = (gallery, draggedPublicId, targetPublicId) => {
  const sourceIndex = gallery.findIndex((asset) => asset.public_id === draggedPublicId);
  const targetIndex = gallery.findIndex((asset) => asset.public_id === targetPublicId);
  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return gallery;
  const nextGallery = [...gallery];
  const [draggedAsset] = nextGallery.splice(sourceIndex, 1);
  nextGallery.splice(targetIndex, 0, draggedAsset);
  return nextGallery;
};

const dashboardField =
  "w-full rounded-[20px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#001935] outline-none transition placeholder:text-[#8c8578] focus:border-[#D4AF37]/60 focus:ring-2 focus:ring-[#D4AF37]/20";
const panelClass = "rounded-[32px] border border-[#001935]/10 bg-white/90 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)] md:p-8";
const PUBLIC_DOMAIN = "sgpropportal.com";
const PRODUCTION_HOSTS = new Set([PUBLIC_DOMAIN, `www.${PUBLIC_DOMAIN}`]);

const sanitizeProfilePathSlug = (value) => {
  const rawValue = String(value || "").trim().toLowerCase();
  if (!rawValue) return null;

  let candidate = rawValue.split("://").pop();
  candidate = candidate.split("/")[0].split("?")[0].split("#")[0].split(":")[0];
  if (candidate.startsWith("www.")) candidate = candidate.slice(4);
  if (candidate === PUBLIC_DOMAIN) return null;
  if (candidate.endsWith(`.${PUBLIC_DOMAIN}`)) {
    candidate = candidate.slice(0, -(PUBLIC_DOMAIN.length + 1)).split(".").pop();
  }
  candidate = candidate.replace(/(?:-)?sgpropportal-com$/, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return candidate || null;
};
const leadActivityIcon = {
  pdf_download: "📄",
  listing_view: "👁️",
  cockpit_open: "📍",
  scenario_switch: "⚖️",
  tax_profile_model: "📍",
  lead_capture: "🧾",
  advisor_report_request: "🤖",
  listing_handoff: "🤝",
  viewing_request: "📅",
  lead_status_change: "🟡",
};

const LEAD_SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "homepage", label: "Homepage" },
  { value: "advisor", label: "Advisor" },
  { value: "handoff", label: "Handoff" },
  { value: "viewing", label: "Viewing" },
  { value: "brochure", label: "Brochure" },
  { value: "agent_portal", label: "Agent Portal" },
];

const LEAD_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "viewing_booked", label: "Viewing Booked" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const leadSourceTone = {
  homepage: "border-[#D4AF37]/35 bg-[#fff4db] text-[#8b6d16]",
  advisor: "border-[#c7d5f5] bg-[#eef4ff] text-[#35548f]",
  handoff: "border-[#d9c4fb] bg-[#f6efff] text-[#6b4fa6]",
  viewing: "border-emerald-200 bg-emerald-50 text-emerald-700",
  brochure: "border-[#d8d8d8] bg-[#f7f7f7] text-[#4e5968]",
  agent_portal: "border-[#c8dfd8] bg-[#eefaf5] text-[#2e6b59]",
};

const leadStatusTone = {
  new: "border-[#d8d8d8] bg-[#f7f7f7] text-[#4e5968]",
  contacted: "border-[#c7d5f5] bg-[#eef4ff] text-[#35548f]",
  qualified: "border-[#d9c4fb] bg-[#f6efff] text-[#6b4fa6]",
  viewing_booked: "border-emerald-200 bg-emerald-50 text-emerald-700",
  won: "border-[#c8dfd8] bg-[#eefaf5] text-[#2e6b59]",
  lost: "border-red-200 bg-red-50 text-red-600",
};

const LeadSignalPill = ({ label, tone, testId }) => (
  <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${tone}`} data-testid={testId}>
    {label}
  </span>
);

const AdminStatCard = ({ label, value, testId }) => (
  <div className="rounded-[26px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">{label}</p>
    <p className="mt-3 text-3xl font-semibold text-[#001935]">{value}</p>
  </div>
);

const formatMagicDropReviewPrice = (value) => {
  const numericValue = Number(value || 0);
  if (!numericValue) return "Price not detected";
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const getMagicDropDistrictSignalPresentation = (listing) => {
  const source = listing?.district_source || "fallback_default";
  if (source === "explicit_pdf_text") {
    return {
      badgeClassName: "border border-emerald-200 bg-emerald-50 text-emerald-700",
      helperText: "District matched directly from the uploaded PDF or extracted source text.",
      label: listing?.district_source_label || "Explicit PDF text",
    };
  }
  if (source === "postal_code_inferred") {
    return {
      badgeClassName: "border border-amber-200 bg-amber-50 text-amber-700",
      helperText: "District was inferred from the detected Singapore postal code.",
      label: listing?.district_source_label || "Postal code inferred",
    };
  }
  if (source === "existing_listing") {
    return {
      badgeClassName: "border border-[#D4AF37]/35 bg-[#fff7e7] text-[#8b6d16]",
      helperText: "No new district clue was found in this upload, so the existing draft value was retained. Review before publishing.",
      label: listing?.district_source_label || "Existing listing value",
    };
  }
  if (source === "missing_required") {
    return {
      badgeClassName: "border border-[#D4AF37]/35 bg-[#fff7e7] text-[#8b6d16]",
      helperText: "District could not be determined from the upload. Set the correct district in Listings before publishing this draft.",
      label: listing?.district_source_label || "District required",
    };
  }
  return {
    badgeClassName: "border border-[#D4AF37]/35 bg-[#fff7e7] text-[#8b6d16]",
    helperText: "No strong district signal was detected, so the default fallback was applied. Review before publishing.",
    label: listing?.district_source_label || "Fallback default",
  };
};

const buildMagicDropSourceSnippet = (rawText, listing) => {
  const lines = String(rawText || "")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return "No raw source text was available for this import.";

  const title = String(listing?.title || "").trim().toLowerCase();
  const significantTokens = title.split(/\s+/).filter((token) => token.length >= 4);
  const priceTokens = [String(Math.round(Number(listing?.price || 0))), String(Number(listing?.price || 0)).replace(/\.0+$/, "")].filter(Boolean);

  const matchedLine =
    lines.find((line) => title && line.toLowerCase().includes(title)) ||
    lines.find((line) => significantTokens.some((token) => line.toLowerCase().includes(token))) ||
    lines.find((line) => priceTokens.some((token) => token && line.includes(token))) ||
    lines[0];

  return matchedLine.length > 320 ? `${matchedLine.slice(0, 320)}…` : matchedLine;
};

export const AdminDashboardPage = ({ onLogout, onSessionRefresh, user }) => {
  const navigate = useNavigate();
  const isPendingAgent = user.role === "pending_agent";
  const isAgentView = ["agent", "pending_agent"].includes(user.role);
  const canManageUsers = user.role === "admin";
  const canViewLeadCRM = ["admin", "agent"].includes(user.role);
  const canAccessInternalOps = !isAgentView;
  const isReadOnlyAgent = isReadOnlySubscription(user);
  const canUsePropNexImport = hasPlanFeature(user, "propnexImport");
  const canUseSalesCommandCenter = hasPlanFeature(user, "salesCommandCenter");
  const canUseInvestmentTools = hasPlanFeature(user, "investmentTools");
  const canUseTeamManagement = hasPlanFeature(user, "teamManagement");
  const canUsePrivateTransactions = canAccessInternalOps || (user.role === "agent" && canUseInvestmentTools);
  const canViewSyncStatus = canAccessInternalOps || (isAgentView && canUsePropNexImport);
  const isPrivateShowroom = Boolean(isPendingAgent && user.is_ambassador_portal && user.showroom_status !== "public_live");
  const showAmbassadorFoundingPanels = Boolean(user.is_ambassador_portal);
  const showFoundingSeatLedger = Boolean(canAccessInternalOps || user.is_ambassador_portal);
  const isTeamLeaderView = user.role === "agent" && canUseTeamManagement && user.team_member_role !== "junior";
  const fileInputRef = useRef(null);
  const usersTabRef = useRef(null);
  const listingManagementFormRef = useRef(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ counts: {}, recent_listings: [], recent_leads: [], activation_audit_entries: [], lead_pipeline: { total: 0, hot: 0, viewing: 0, sources: [], statuses: [] }, analytics: { overview: {}, engagement_funnel: [], shortlist: { top_listings: [] }, onboarding: { recent_completions: [] }, recent_events: [] }, founding_membership: { total_spots: 18, occupied_count: 6, remaining_count: 12 }, blueprint: { database_schema: {}, api_endpoints: [] } });
  const [launchReadiness, setLaunchReadiness] = useState({ checked_at: "", cards: [], closer_rows: [], production_slots: [], twilio_webhook_path: "", environment_mode: "sandbox" });
  const [options, setOptions] = useState({ districts: [], tenures: [], property_types: [] });
  const [listings, setListings] = useState([]);
  const [listingForm, setListingForm] = useState(makeListingForm());
  const [editingListingId, setEditingListingId] = useState("");
  const [sovereignStudioListingId, setSovereignStudioListingId] = useState("");
  const [users, setUsers] = useState([]);
  const [referralSummary, setReferralSummary] = useState({ overview: {}, partners: [] });
  const [leads, setLeads] = useState([]);
  const [userDrafts, setUserDrafts] = useState({});
  const [newUser, setNewUser] = useState(makeNewUserForm());
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [userDrawerMode, setUserDrawerMode] = useState("create");
  const [editingUserId, setEditingUserId] = useState("");
  const [syncingPortfolio, setSyncingPortfolio] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ status: "idle", message: "No manual sync has been run yet" });
  const [showAgentImportModal, setShowAgentImportModal] = useState(false);
  const [syncConsentChecked, setSyncConsentChecked] = useState(false);
  const [lockedFeatureKey, setLockedFeatureKey] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingFloorplanPdf, setUploadingFloorplanPdf] = useState(false);
  const [uploadingSiteplanPdf, setUploadingSiteplanPdf] = useState(false);
  const [privateTransactionSummary, setPrivateTransactionSummary] = useState({ total_records: 0, district_count: 0, project_count: 0, last_upload_at: null, districts_covered: [], recent_uploads: [], scope_label: "", owner_name: "" });
  const [privateTransactionForm, setPrivateTransactionForm] = useState(makePrivateTransactionForm(user, isAgentView));
  const [privateTransactionFile, setPrivateTransactionFile] = useState(null);
  const [privateTransactionUploading, setPrivateTransactionUploading] = useState(false);
  const [magicDropFile, setMagicDropFile] = useState(null);
  const [magicDropUploading, setMagicDropUploading] = useState(false);
  const [magicDropDragActive, setMagicDropDragActive] = useState(false);
  const [magicDropReviewData, setMagicDropReviewData] = useState(null);
  const [showMagicDropReviewSheet, setShowMagicDropReviewSheet] = useState(false);
  const [magicDropApprovedListingIds, setMagicDropApprovedListingIds] = useState([]);
  const [magicDropPublishing, setMagicDropPublishing] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [hasPendingWelcomeSignature] = useState(() => getPendingWelcomeSignatureFlag());
  const [showSignatureWelcomeModal, setShowSignatureWelcomeModal] = useState(() => getPendingWelcomeSignatureFlag());
  const [showAmbassadorClaimModal, setShowAmbassadorClaimModal] = useState(false);
  const [claimVerifySubmitting, setClaimVerifySubmitting] = useState(false);
  const [showFoundingCelebration, setShowFoundingCelebration] = useState(false);
  const [creatingReferralPartner, setCreatingReferralPartner] = useState(false);
  const [rehearsingPartnerId, setRehearsingPartnerId] = useState("");
  const [updatingReferralCommissionId, setUpdatingReferralCommissionId] = useState("");
  const [leadComplianceDraft, setLeadComplianceDraft] = useState({ cdd_completed: false, source_of_funds_verified: false, sanctions_screened: false, beneficial_owner_checked: false, risk_notes: "", deal_stage: "" });
  const [leadQuickFilter, setLeadQuickFilter] = useState("all");
  const [leadSourceFilter, setLeadSourceFilter] = useState("all");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [leadOwnerFilter, setLeadOwnerFilter] = useState("");
  const [leadStatusDraft, setLeadStatusDraft] = useState({ lead_status: "new", note: "" });
  const [savingLeadStatus, setSavingLeadStatus] = useState(false);
  const [userPlanFilter, setUserPlanFilter] = useState("all");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSort, setUserSort] = useState({ key: "created_at", direction: "desc" });
  const [listingFilters, setListingFilters] = useState({ search: "", district: "", status: "", owner_id: "", min_price: "", max_price: "", under_valued_only: false, sort_by: "updated_at_desc" });
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [bulkStatusValue, setBulkStatusValue] = useState("Sold Out");
  const [expandedInvestmentRows, setExpandedInvestmentRows] = useState([]);
  const [investmentForecastCache, setInvestmentForecastCache] = useState({});
  const [loadingInvestmentRows, setLoadingInvestmentRows] = useState([]);
  const ownerOptions = useMemo(() => users.filter((member) => member.role === "agent"), [users]);
  const activationBypassCandidates = useMemo(() => users.filter((member) => member.activation_status !== "active_agent"), [users]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamComplianceHealth, setTeamComplianceHealth] = useState(null);
  const hasPersistedInitialDashboardTab = useRef(false);
  const [networkSummary, setNetworkSummary] = useState({ overview: {}, incoming_requests: [], connected_agents: [], platform_referrals: { linked: false, partner_name: null, overview: {}, recent_commissions: [], status_label: "No linked platform referral ledger yet." }, deal_referrals: { overview: {}, recent_posted: [], mocked_summary: { has_mocked_data: true, settlement_status: "MOCKED", projected_pipeline_earnings: 0, projected_closed_earnings: 0, note: "Projected deal-referral earnings are MOCKED until settlement tracking and closed co-broking reconciliation go live." } } });
  const [networkDirectory, setNetworkDirectory] = useState([]);
  const [networkDeals, setNetworkDeals] = useState([]);
  const [networkFilters, setNetworkFilters] = useState({ specialty: "", target_district: "", language: "" });
  const [networkLoading, setNetworkLoading] = useState(false);
  const [connectingAgentId, setConnectingAgentId] = useState("");
  const [requestsBusyId, setRequestsBusyId] = useState("");
  const [networkDealForm, setNetworkDealForm] = useState(makeNetworkDealForm());
  const [postingNetworkDeal, setPostingNetworkDeal] = useState(false);
  const [marketingCampaigns, setMarketingCampaigns] = useState([]);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingSelectedListingId, setMarketingSelectedListingId] = useState("");
  const [marketingSelectedCampaign, setMarketingSelectedCampaign] = useState(null);
  const [marketingPosting, setMarketingPosting] = useState(false);
  const [marketingUpdating, setMarketingUpdating] = useState(false);
  const [marketingSocialAccounts, setMarketingSocialAccounts] = useState([]);
  const [marketingSocialAccountsLoading, setMarketingSocialAccountsLoading] = useState(false);
  const [marketingSocialAccountDrafts, setMarketingSocialAccountDrafts] = useState(makeMarketingSocialAccountDrafts());
  const [marketingConnectingPlatform, setMarketingConnectingPlatform] = useState("");
  const [marketingTimeline, setMarketingTimeline] = useState([]);
  const [marketingTimelineLoading, setMarketingTimelineLoading] = useState(false);
  const [teamInvite, setTeamInvite] = useState({ name: "", email: "", cea_registration_number: "", phone: "", team_member_status: "Active" });
  const [teamBranding, setTeamBranding] = useState({ team_name: user.team_name || "", team_logo_url: user.team_logo_url || "" });
  const [billingState, setBillingState] = useState({ tone: "idle", title: "", body: "" });
  const [billingBusyKey, setBillingBusyKey] = useState("");
  const [showAgentProfileSettingsSheet, setShowAgentProfileSettingsSheet] = useState(false);
  const [adminBypassSubmitting, setAdminBypassSubmitting] = useState(false);
  const [sendingSignaturePreviewAgentId, setSendingSignaturePreviewAgentId] = useState("");
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const editingUser = useMemo(() => users.find((member) => member.id === editingUserId) || null, [editingUserId, users]);
  const editingUserDraft = useMemo(() => (editingUser ? userDrafts[editingUser.id] || makeUserDraft(editingUser) : null), [editingUser, userDrafts]);
  const cleanAgentSubdomainSlug = useMemo(() => sanitizeProfilePathSlug(user.subdomain_slug), [user.subdomain_slug]);
  const currentPublicOrigin = useMemo(() => {
    if (typeof window === "undefined") return `https://${PUBLIC_DOMAIN}`;
    const { hostname, origin } = window.location;
    if (PRODUCTION_HOSTS.has(hostname)) return `https://${PUBLIC_DOMAIN}`;
    return origin;
  }, []);
  const privateShowroomHref = useMemo(() => {
    if (!cleanAgentSubdomainSlug) return currentPublicOrigin;
    const baseHref = `${currentPublicOrigin}/agent/${cleanAgentSubdomainSlug}`;
    if (!isPrivateShowroom || !user.gallery_preview_token) return baseHref;
    return `${baseHref}?preview_token=${encodeURIComponent(user.gallery_preview_token)}`;
  }, [cleanAgentSubdomainSlug, currentPublicOrigin, isPrivateShowroom, user.gallery_preview_token]);
  const publicSiteHref = useMemo(
    () => (isPrivateShowroom ? privateShowroomHref : (cleanAgentSubdomainSlug ? `${currentPublicOrigin}/agent/${cleanAgentSubdomainSlug}` : currentPublicOrigin)),
    [cleanAgentSubdomainSlug, currentPublicOrigin, isPrivateShowroom, privateShowroomHref],
  );
  const publicSiteDisplay = useMemo(() => {
    try {
      const url = new URL(publicSiteHref);
      return `${url.host}${url.pathname}`;
    } catch {
      return publicSiteHref;
    }
  }, [publicSiteHref]);
  const missingComplianceLabels = useMemo(() => {
    const labels = [];
    if (!user?.name) labels.push("full name");
    if (!user?.subdomain_slug) labels.push("custom URL slug");
    if (!user?.phone) labels.push("contact number");
    if (!user?.cea_registration_number) labels.push("CEA registration number");
    if (!user?.parent_agency_name) labels.push("parent agency name");
    if (!user?.agency_license_number) labels.push("agency license number");
    if (!user?.profile_photo_url) labels.push("profile headshot");
    return labels;
  }, [user]);
  const needsProfileSetupPrompt = isAgentView && missingComplianceLabels.length > 0;
  const pendingAttestationCount = useMemo(
    () => listings.filter((listing) => listing.status === "Awaiting Agent Attestation").length,
    [listings],
  );

  useSeo({
    title: `${isAgentView ? "Agent" : "Admin"} Dashboard | SG Prop Portal`,
    description: isAgentView
      ? "Private portfolio workspace for SG Prop Portal agents with listing control and scoped lead visibility."
      : "Production-grade admin console for SG Prop Portal with listing search, media management, bulk actions, and external portfolio sync diagnostics.",
  });

  const tabs = useMemo(
    () => {
      if (isAgentView) {
        return [
          { key: "summary", label: "Summary" },
          { key: "marketing", label: "Marketing Studio" },
          ...(user.role === "agent" ? [{ key: "network", label: "Network" }] : []),
          ...(user.role === "agent" ? [{ key: "create-listing", label: "Create Listing" }] : []),
          { key: "listings", label: "Listings" },
          ...(user.role === "agent" ? [{ key: "leads", label: "Leads" }] : []),
          ...(isTeamLeaderView ? [{ key: "team", label: "Elite Team" }] : []),
        ];
      }
      return [
        { key: "summary", label: "Summary" },
        { key: "marketing", label: "Marketing Studio" },
        { key: "launch-checklist", label: "Mission Control" },
        { key: "market-insights", label: "Market Insights" },
        ...(canManageUsers ? [{ key: "partners", label: "Partner Ledger" }] : []),
        { key: "create-listing", label: "Create Listing" },
        { key: "listings", label: "Listings" },
        ...(canViewLeadCRM ? [{ key: "leads", label: "Leads" }] : []),
        ...(canManageUsers ? [{ key: "users", label: "Users" }] : []),
        { key: "blueprint", label: "Blueprint" },
      ];
    },
    [canManageUsers, canViewLeadCRM, isAgentView, isTeamLeaderView, user.role],
  );

  useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab("summary");
    }
  }, [activeTab, tabs]);

  useEffect(() => {
    if (!selectedLead) return;
    setLeadComplianceDraft({
      cdd_completed: Boolean(selectedLead.compliance_checklist?.cdd_completed),
      source_of_funds_verified: Boolean(selectedLead.compliance_checklist?.source_of_funds_verified),
      sanctions_screened: Boolean(selectedLead.compliance_checklist?.sanctions_screened),
      beneficial_owner_checked: Boolean(selectedLead.compliance_checklist?.beneficial_owner_checked),
      risk_notes: selectedLead.compliance_risk_notes || "",
      deal_stage: selectedLead.deal_stage || "",
    });
    setLeadStatusDraft({
      lead_status: selectedLead.lead_status || "new",
      note: selectedLead.latest_status_note || "",
    });
  }, [selectedLead]);

  useEffect(() => {
    if (isAgentView || privateTransactionForm.owner_id || !ownerOptions.length) return;
    const preferredOwner = ownerOptions.find((member) => sanitizeProfilePathSlug(member.subdomain_slug) === "clive") || ownerOptions[0];
    if (!preferredOwner) return;
    setPrivateTransactionForm((current) => ({ ...current, owner_id: preferredOwner.id }));
  }, [isAgentView, ownerOptions, privateTransactionForm.owner_id]);

  useEffect(() => {
    if (!isPendingAgent || !user?.claim_verification_required) return;
    const storageKey = `${CLAIM_VERIFY_MODAL_KEY_PREFIX}:${user.id}`;
    if (window.localStorage.getItem(storageKey) !== "1") {
      setShowAmbassadorClaimModal(true);
    }
  }, [isPendingAgent, user?.claim_verification_required, user?.id]);

  const loadDashboardMeta = useCallback(async () => {
    const baseRequests = [
      api.get("/dashboard/summary", authConfig()),
      canAccessInternalOps ? api.get("/dashboard/launch-readiness", authConfig()) : Promise.resolve({ data: { checked_at: "", cards: [] } }),
      api.get("/search-options"),
      canViewSyncStatus ? api.get("/dashboard/sync-propnex-status", authConfig()) : Promise.resolve({ data: { status: "idle", message: "Your workspace is limited to your own listings and listing-driven leads.", warnings: [], price_guardrail: {} } }),
      canUsePrivateTransactions ? api.get("/dashboard/private-transactions/summary", authConfig({ params: { owner_id: !isAgentView ? privateTransactionForm.owner_id || undefined : undefined } })) : Promise.resolve({ data: { total_records: 0, district_count: 0, project_count: 0, last_upload_at: null, districts_covered: [], recent_uploads: [], scope_label: "", owner_name: "" } }),
    ];
    const roleRequests = [
      canManageUsers ? api.get("/users", authConfig({ params: { role: "all", plan_type: userPlanFilter || "all" } })) : Promise.resolve(null),
      canManageUsers ? api.get("/referrals/summary", authConfig()) : Promise.resolve(null),
      canViewLeadCRM ? api.get("/leads", authConfig({ params: { owner_id: leadOwnerFilter || undefined } })) : Promise.resolve(null),
      isAgentView && isTeamLeaderView ? api.get("/team/members", authConfig()) : Promise.resolve(null),
      isAgentView && isTeamLeaderView ? api.get("/team/compliance-health", authConfig()) : Promise.resolve(null),
    ];
    const responses = await Promise.all([...baseRequests, ...roleRequests]);
    const [summaryResponse, launchReadinessResponse, optionsResponse, syncStatusResponse, privateTransactionSummaryResponse, usersResponse, referralSummaryResponse, leadsResponse, teamMembersResponse, teamComplianceResponse] = responses;
    setSummary(summaryResponse.data);
    setLaunchReadiness(launchReadinessResponse.data);
    setOptions(optionsResponse.data);
    setSyncStatus(syncStatusResponse.data);
    setPrivateTransactionSummary(privateTransactionSummaryResponse.data);

    if (usersResponse?.data) {
      setUsers(usersResponse.data);
      setUserDrafts(
        Object.fromEntries(
          usersResponse.data.map((member) => [member.id, makeUserDraft(member)]),
        ),
      );
    } else {
      setUsers([]);
      setUserDrafts({});
    }

    if (referralSummaryResponse?.data) {
      setReferralSummary(referralSummaryResponse.data);
    } else {
      setReferralSummary({ overview: {}, partners: [] });
    }

    if (leadsResponse?.data) {
      setLeads(leadsResponse.data);
    } else {
      setLeads([]);
    }

    if (teamMembersResponse?.data) {
      setTeamMembers(teamMembersResponse.data);
    } else {
      setTeamMembers([]);
    }

    if (teamComplianceResponse?.data) {
      setTeamComplianceHealth(teamComplianceResponse.data);
    } else {
      setTeamComplianceHealth(null);
    }
  }, [canAccessInternalOps, canManageUsers, canUsePrivateTransactions, canViewLeadCRM, canViewSyncStatus, isAgentView, isTeamLeaderView, leadOwnerFilter, privateTransactionForm.owner_id, userPlanFilter]);

  const loadNetworkWorkspace = useCallback(async (filtersOverride = networkFilters) => {
    if (!isAgentView || user.role !== "agent") return;
    setNetworkLoading(true);
    try {
      const [agentsResponse, dealsResponse, summaryResponse] = await Promise.all([
        api.get("/network/agents", authConfig({ params: { specialty: filtersOverride.specialty || undefined, target_district: filtersOverride.target_district || undefined, language: filtersOverride.language || undefined } })),
        api.get("/network/deals", authConfig()),
        api.get("/network/summary", authConfig()),
      ]);
      setNetworkDirectory(agentsResponse.data || []);
      setNetworkDeals(dealsResponse.data || []);
      setNetworkSummary(summaryResponse.data || { overview: {}, incoming_requests: [], connected_agents: [], platform_referrals: { linked: false, partner_name: null, overview: {}, recent_commissions: [], status_label: "No linked platform referral ledger yet." }, deal_referrals: { overview: {}, recent_posted: [], mocked_summary: { has_mocked_data: true, settlement_status: "MOCKED", projected_pipeline_earnings: 0, projected_closed_earnings: 0, note: "Projected deal-referral earnings are MOCKED until settlement tracking and closed co-broking reconciliation go live." } } });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load the network workspace"));
    } finally {
      setNetworkLoading(false);
    }
  }, [isAgentView, user.role]);

  useEffect(() => {
    if (!isAgentView || user.role !== "agent" || activeTab !== "network") return;
    loadNetworkWorkspace();
  }, [activeTab, isAgentView, loadNetworkWorkspace, user.role]);

  const updateNetworkFilter = useCallback((key, value) => {
    setNetworkFilters((current) => {
      const next = { ...current, [key]: value };
      void loadNetworkWorkspace(next);
      return next;
    });
  }, [loadNetworkWorkspace]);

  const connectNetworkAgent = useCallback(async (agentId) => {
    setConnectingAgentId(agentId);
    try {
      const response = await api.post(`/network/connect/${agentId}`, {}, authConfig());
      toast.success(response.data?.message || "Connection request sent");
      await loadNetworkWorkspace();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to send connection request"));
    } finally {
      setConnectingAgentId("");
    }
  }, [loadNetworkWorkspace]);

  const acceptNetworkRequest = useCallback(async (agentId) => {
    setRequestsBusyId(agentId);
    try {
      const response = await api.post(`/network/accept/${agentId}`, {}, authConfig());
      toast.success(response.data?.message || "Connection request accepted");
      await loadNetworkWorkspace();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to accept connection request"));
    } finally {
      setRequestsBusyId("");
    }
  }, [loadNetworkWorkspace]);

  const postNetworkDeal = useCallback(async (onSuccess) => {
    setPostingNetworkDeal(true);
    try {
      await api.post("/network/deals", networkDealForm, authConfig());
      toast.success("Lead posted to the B2B exchange");
      setNetworkDealForm(makeNetworkDealForm());
      if (onSuccess) onSuccess();
      await loadNetworkWorkspace();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to post this B2B lead"));
    } finally {
      setPostingNetworkDeal(false);
    }
  }, [loadNetworkWorkspace, networkDealForm]);

  const loadMarketingCampaigns = useCallback(async () => {
    if (!tabs.some((tab) => tab.key === "marketing")) return;
    setMarketingLoading(true);
    try {
      const response = await api.get("/marketing/campaigns", authConfig());
      setMarketingCampaigns(response.data || []);
      setMarketingSelectedCampaign((current) => {
        if (!current) return response.data?.[0] || null;
        return response.data?.find((item) => item.id === current.id) || response.data?.[0] || null;
      });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load marketing campaigns"));
    } finally {
      setMarketingLoading(false);
    }
  }, [tabs]);

  useEffect(() => {
    if (activeTab !== "marketing") return;
    loadMarketingCampaigns();
  }, [activeTab, loadMarketingCampaigns]);

  const hydrateMarketingSocialAccountDrafts = useCallback((accounts) => {
    setMarketingSocialAccountDrafts((current) => {
      const next = { ...makeMarketingSocialAccountDrafts(), ...current };
      SOCIAL_PLATFORM_ORDER.forEach((platform) => {
        const account = (accounts || []).find((item) => item.platform === platform);
        if (account?.platform_page_id) next[platform] = account.platform_page_id;
      });
      return next;
    });
  }, []);

  const loadMarketingSocialAccounts = useCallback(async () => {
    if (!tabs.some((tab) => tab.key === "marketing")) return;
    setMarketingSocialAccountsLoading(true);
    try {
      const response = await api.get("/marketing/social-accounts", authConfig());
      const accounts = response.data || [];
      setMarketingSocialAccounts(accounts);
      hydrateMarketingSocialAccountDrafts(accounts);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load social accounts"));
    } finally {
      setMarketingSocialAccountsLoading(false);
    }
  }, [hydrateMarketingSocialAccountDrafts, tabs]);

  useEffect(() => {
    if (activeTab !== "marketing") return;
    loadMarketingSocialAccounts();
  }, [activeTab, loadMarketingSocialAccounts]);

  const loadMarketingTimeline = useCallback(async (campaignId) => {
    if (!campaignId) {
      setMarketingTimeline([]);
      return;
    }
    setMarketingTimelineLoading(true);
    try {
      const response = await api.get(`/marketing/campaigns/${campaignId}/timeline`, authConfig());
      setMarketingTimeline(response.data || []);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load campaign timeline"));
    } finally {
      setMarketingTimelineLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "marketing") return;
    if (!marketingSelectedCampaign?.id) {
      setMarketingTimeline([]);
      return;
    }
    loadMarketingTimeline(marketingSelectedCampaign.id);
  }, [activeTab, loadMarketingTimeline, marketingSelectedCampaign?.id]);

  const createMarketingCampaign = useCallback(async () => {
    if (!marketingSelectedListingId) {
      toast.error("Select a listing to initialize a campaign draft.");
      return;
    }
    setMarketingPosting(true);
    try {
      const response = await api.post("/marketing/campaigns", { listing_id: marketingSelectedListingId }, authConfig());
      toast.success("Marketing campaign draft initialized");
      setMarketingSelectedCampaign(response.data);
      setMarketingSelectedListingId("");
      await loadMarketingCampaigns();
      await loadMarketingTimeline(response.data?.id);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to initialize a marketing draft"));
    } finally {
      setMarketingPosting(false);
    }
  }, [loadMarketingCampaigns, loadMarketingTimeline, marketingSelectedListingId]);

  const editMarketingCampaignField = useCallback((platformKey, fieldKey, value) => {
    setMarketingSelectedCampaign((current) => {
      if (!current) return current;
      if (platformKey === "root") {
        return { ...current, [fieldKey]: value };
      }
      return {
        ...current,
        content_payloads: {
          ...current.content_payloads,
          [platformKey]: {
            ...current.content_payloads[platformKey],
            [fieldKey]: value,
          },
        },
      };
    });
  }, []);

  const editMarketingSocialAccountDraft = useCallback((platform, value) => {
    setMarketingSocialAccountDrafts((current) => ({ ...current, [platform]: value }));
  }, []);

  const connectMarketingSocialAccount = useCallback(async (platform) => {
    setMarketingConnectingPlatform(platform);
    try {
      const response = await api.post(
        "/marketing/social-accounts/connect",
        {
          platform,
          platform_page_id: marketingSocialAccountDrafts[platform] || `${platform}-${isAgentView ? "personal" : "official"}`,
        },
        authConfig(),
      );
      const nextAccounts = [...(marketingSocialAccounts || []).filter((item) => item.platform !== platform), response.data];
      const sortedAccounts = SOCIAL_PLATFORM_ORDER.map((orderedPlatform) => nextAccounts.find((item) => item.platform === orderedPlatform)).filter(Boolean);
      setMarketingSocialAccounts(sortedAccounts);
      hydrateMarketingSocialAccountDrafts(sortedAccounts);
      if (marketingSelectedCampaign?.id) {
        await loadMarketingTimeline(marketingSelectedCampaign.id);
      }
      const platformLabel = platform === "x" ? "X" : platform === "linkedin" ? "LinkedIn" : `${platform.charAt(0).toUpperCase()}${platform.slice(1)}`;
      toast.success(`${platformLabel} account connected successfully`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to connect this social account"));
    } finally {
      setMarketingConnectingPlatform("");
    }
  }, [hydrateMarketingSocialAccountDrafts, isAgentView, loadMarketingTimeline, marketingSelectedCampaign?.id, marketingSocialAccountDrafts, marketingSocialAccounts]);

  const approveAndScheduleMarketingCampaign = useCallback(async () => {
    if (!marketingSelectedCampaign?.id) {
      toast.error("Select a campaign draft first.");
      return;
    }
    setMarketingUpdating(true);
    try {
      const scheduledFor = fromMarketingInputValue(marketingSelectedCampaign.scheduled_for) || null;
      const response = await api.put(`/marketing/campaigns/${marketingSelectedCampaign.id}`, {
        content_payloads: marketingSelectedCampaign.content_payloads,
        scheduled_for: scheduledFor,
        campaign_status: scheduledFor ? "Scheduled" : "Approved",
      }, authConfig());
      const publishStatus = response.data?.publishing_result?.status;
      const publishMessage = response.data?.publishing_result?.message;
      if (publishStatus === "publishing_in_progress") {
        toast.success(publishMessage || "Publishing has started. Final confirmation will appear automatically.");
      } else if (publishStatus === "partial") {
        toast.error(publishMessage || "Published to some platforms, but one or more platforms still need attention.");
      } else {
        toast.success(publishMessage || (scheduledFor ? "Campaign scheduled to the social queue" : "Campaign is now publishing live"));
      }
      setMarketingSelectedCampaign(response.data);
      await loadMarketingCampaigns();
      await loadMarketingTimeline(response.data?.id);
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail || "Unable to update this marketing campaign");
      if (String(detail).toLowerCase().includes("partially configured") || String(detail).toLowerCase().includes("ayrshare credentials")) {
        toast.error("Live publishing isn’t fully configured yet. Please complete the social provider credentials or keep using scheduled/mock preview mode.");
      } else {
        toast.error(detail);
      }
    } finally {
      setMarketingUpdating(false);
    }
  }, [loadMarketingCampaigns, loadMarketingTimeline, marketingSelectedCampaign]);

  const stageMarketingCampaignFromListing = useCallback(async (listing) => {
    if (!listing?.id) {
      toast.error("Listing not available for marketing staging.");
      return;
    }
    setMarketingSelectedListingId(listing.id);
    setActiveTab("marketing");
    setMarketingPosting(true);
    try {
      const response = await api.post("/marketing/campaigns", { listing_id: listing.id }, authConfig());
      toast.success("Listing moved into Marketing Studio");
      setMarketingSelectedCampaign(response.data);
      await loadMarketingCampaigns();
      await loadMarketingTimeline(response.data?.id);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to stage this listing in Marketing Studio"));
    } finally {
      setMarketingPosting(false);
    }
  }, [loadMarketingCampaigns, loadMarketingTimeline]);

  const loadListings = useCallback(async () => {
    const params = {
      limit: 500,
      search: listingFilters.search || undefined,
      district: listingFilters.district || undefined,
      status: listingFilters.status || undefined,
      min_price: listingFilters.min_price ? Number(listingFilters.min_price) : undefined,
      max_price: listingFilters.max_price ? Number(listingFilters.max_price) : undefined,
      owner_id: listingFilters.owner_id || undefined,
      under_valued_only: listingFilters.under_valued_only || undefined,
      sort_by: listingFilters.sort_by || undefined,
    };
    const response = await api.get("/dashboard/listings", authConfig({ params }));
    setListings(response.data);
  }, [listingFilters]);

  const openUpgradeModal = (featureKey) => {
    setLockedFeatureKey(featureKey);
  };

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadDashboardMeta(), loadListings()]);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [loadDashboardMeta, loadListings]);

  const createReferralPartner = useCallback(async (payload) => {
    setCreatingReferralPartner(true);
    try {
      await api.post("/referrals/partners", payload, authConfig());
      toast.success("Referral partner created");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to create referral partner"));
    } finally {
      setCreatingReferralPartner(false);
    }
  }, [loadDashboardMeta]);

  const runAlexNgReferralRehearsal = useCallback(async (partnerId) => {
    setRehearsingPartnerId(partnerId);
    try {
      const response = await api.post("/referrals/rehearsals/alex-ng", { partner_id: partnerId }, authConfig());
      toast.success(response.data.message || "Alex Ng rehearsal logged");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to run the Alex Ng rehearsal"));
    } finally {
      setRehearsingPartnerId("");
    }
  }, [loadDashboardMeta]);

  const updateReferralCommissionStatus = useCallback(async (commissionId, payoutStatus) => {
    setUpdatingReferralCommissionId(commissionId);
    try {
      await api.patch(`/referrals/commissions/${commissionId}`, { payout_status: payoutStatus }, authConfig());
      toast.success(`Commission marked ${payoutStatus}`);
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to update the payout state"));
    } finally {
      setUpdatingReferralCommissionId("");
    }
  }, [loadDashboardMeta]);

  const clearBillingQueryParams = useCallback(() => {
    const url = new URL(window.location.href);
    ["billing_status", "package_id", "session_id", "welcome_signature", "from_onboarding", "founding_member"].forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const persistDashboardTabQuery = useCallback((tabKey) => {
    const url = new URL(window.location.href);
    if (tabKey) {
      url.searchParams.set("tab", tabKey);
    } else {
      url.searchParams.delete("tab");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const closeSignatureWelcomeModal = useCallback(() => {
    window.sessionStorage.removeItem(PENDING_WELCOME_SIGNATURE_KEY);
    setShowSignatureWelcomeModal(false);
  }, []);

  const closeAmbassadorClaimModal = useCallback(() => {
    if (user?.id) {
      window.localStorage.setItem(`${CLAIM_VERIFY_MODAL_KEY_PREFIX}:${user.id}`, "1");
    }
    setShowAmbassadorClaimModal(false);
  }, [user?.id]);

  const runAdminActivationBypass = useCallback(async (agentId) => {
    if (!agentId) return;
    setAdminBypassSubmitting(true);
    try {
      const response = await api.post("/dashboard/activation-audit/admin-bypass", { agent_id: agentId }, authConfig());
      toast.success(response.data.message || "Admin bypass completed");
      await loadDashboard();
      if (typeof onSessionRefresh === "function") {
        await onSessionRefresh();
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to run admin bypass"));
    } finally {
      setAdminBypassSubmitting(false);
    }
  }, [loadDashboard, onSessionRefresh]);

  const sendSignaturePreview = useCallback(async (agentId) => {
    if (!agentId) return;
    setSendingSignaturePreviewAgentId(agentId);
    try {
      const response = await api.post("/dashboard/signature-preview", { agent_id: agentId }, authConfig());
      const topListings = (response.data.top_listing_titles || []).filter(Boolean).join(", ");
      toast.success(topListings ? `Signature Preview sent: ${topListings}` : (response.data.message || "Signature Preview sent"));
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to send Signature Preview"));
    } finally {
      setSendingSignaturePreviewAgentId("");
    }
  }, [loadDashboardMeta]);

  const runAdminCleanup = useCallback(async () => {
    setCleanupBusy(true);
    try {
      const response = await api.post("/dashboard/cleanup-rehearsal-data", {}, authConfig());
      setCleanupResult({ ...response.data, tone: "success" });
      toast.success(response.data.message || "Cleanup utility completed");
      await loadDashboard();
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail || "Unable to run cleanup utility");
      setCleanupResult({ message, tone: "error" });
      toast.error(message);
    } finally {
      setCleanupBusy(false);
    }
  }, [loadDashboard]);

  const logMissionControlActivity = useCallback(async (eventType, detail, metadata = {}) => {
    try {
      await api.post("/dashboard/activity-log", { event_type: eventType, detail, metadata }, authConfig());
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to record Mission Control activity"));
    }
  }, [loadDashboardMeta]);

  const handleEnvironmentModeChange = useCallback(async (mode) => {
    await logMissionControlActivity("PRODUCTION_TOGGLE", `Mission Control viewed in ${String(mode || "sandbox").toUpperCase()} mode`, { mode });
  }, [logMissionControlActivity]);

  useEffect(() => {
    if (!user?.founding_member_confetti_pending) return;
    setShowFoundingCelebration(true);
  }, [user?.founding_member_confetti_pending]);

  const acknowledgeFoundingCelebration = useCallback(async () => {
    try {
      await api.post("/dashboard/ambassador/acknowledge-celebration", {}, authConfig());
      await onSessionRefresh();
    } catch {
      // keep the celebratory UX resilient even if the acknowledgement request fails once
    } finally {
      setShowFoundingCelebration(false);
    }
  }, [onSessionRefresh]);

  const startAmbassadorClaimVerify = useCallback(async () => {
    setClaimVerifySubmitting(true);
    try {
      const response = await api.post("/dashboard/ambassador/claim-verify", {}, authConfig());
      toast.success(response.data.message || "Claim & Verify saved");
      closeAmbassadorClaimModal();
      await onSessionRefresh();
      navigate(`/agent/${user.subdomain_slug}/founding-checkout`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to save Claim & Verify"));
    } finally {
      setClaimVerifySubmitting(false);
    }
  }, [closeAmbassadorClaimModal, navigate, onSessionRefresh, user.subdomain_slug]);

  const startCheckout = useCallback(async (packageId) => {
    const billingPackage = getBillingPackage(packageId);
    setBillingBusyKey(packageId);
    try {
      const response = await api.post(
        "/billing/checkout-session",
        { package_id: packageId, origin_url: window.location.origin },
        authConfig(),
      );
      window.location.href = response.data.url;
      return response.data;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || `Unable to start ${billingPackage.title} checkout`));
      throw error;
    } finally {
      setBillingBusyKey("");
    }
  }, []);

  const openBillingPortal = useCallback(async ({ silentFailure = false } = {}) => {
    setBillingBusyKey("portal");
    try {
      const response = await api.post(
        "/billing/portal-session",
        { origin_url: window.location.origin },
        authConfig(),
      );
      window.location.href = response.data.url;
      return response.data;
    } catch (error) {
      if (!silentFailure) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to open Stripe billing portal"));
      }
      throw error;
    } finally {
      setBillingBusyKey("");
    }
  }, []);

  const launchPlanAction = useCallback(async (packageId, { preferPortal = false, portalMessage } = {}) => {
    const currentPackageId = getPackageIdForPlanType(user.plan_type);
    if (preferPortal || (user.subscription_status !== "Trial" && packageId !== currentPackageId)) {
      try {
        await openBillingPortal({ silentFailure: true });
        if (portalMessage) toast.success(portalMessage);
        return;
      } catch {
        // Fall through to checkout creation when the portal path is unavailable.
      }
    }
    await startCheckout(packageId);
  }, [openBillingPortal, startCheckout, user.plan_type, user.subscription_status]);

  const pollCheckoutStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt === 0) {
      setBillingState({
        tone: "pending",
        title: "Checking Stripe payment",
        body: "We’re confirming your subscription status and unlocking the right SaaS tier now.",
      });
    }
    if (attempt >= 6) {
      setBillingState({
        tone: "warning",
        title: "Still waiting for Stripe confirmation",
        body: "Your checkout is still processing. Refresh shortly or reopen the billing flow if needed.",
      });
      clearBillingQueryParams();
      return;
    }
    try {
      const response = await api.get(`/billing/checkout-status/${sessionId}`, authConfig());
      if (response.data.payment_status === "paid") {
        if (hasPendingWelcomeSignature) {
          window.sessionStorage.setItem(PENDING_WELCOME_SIGNATURE_KEY, "1");
          setShowSignatureWelcomeModal(true);
        }
        setBillingState({
          tone: "success",
          title: isPrivateShowroom ? "Private Showroom published" : `${response.data.plan_type || "Subscription"} activated`,
          body: isPrivateShowroom
            ? "Stripe confirmed your payment. Your boutique URL is now public, your listings are eligible for the global marketplace, and lead tracking is live."
            : `Stripe confirmed your payment. ${response.data.subscription_status || "Active"} access is now reflected in this workspace.`,
        });
        await onSessionRefresh?.();
        await loadDashboard();
        clearBillingQueryParams();
        return;
      }
      if (response.data.status === "expired") {
        setBillingState({
          tone: "error",
          title: "Stripe session expired",
          body: "Your checkout session expired before payment was completed. Start the plan action again when ready.",
        });
        clearBillingQueryParams();
        return;
      }
      window.setTimeout(() => pollCheckoutStatus(sessionId, attempt + 1), 2000);
    } catch (error) {
      setBillingState({
        tone: "error",
        title: "Unable to confirm Stripe status",
        body: formatApiErrorDetail(error.response?.data?.detail || "The checkout returned, but status confirmation did not complete."),
      });
      clearBillingQueryParams();
    }
  }, [clearBillingQueryParams, loadDashboard, onSessionRefresh]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const billingStatus = params.get("billing_status");
    const requestedTab = params.get("tab");

    if (requestedTab && tabs.some((tab) => tab.key === requestedTab)) {
      setActiveTab(requestedTab);
    }

    if (sessionId) {
      pollCheckoutStatus(sessionId);
      return;
    }
    if (billingStatus === "cancelled") {
      setBillingState({
        tone: "warning",
        title: "Stripe checkout cancelled",
        body: isPrivateShowroom ? "No payment was made. Your portal remains in Private Showroom mode and hidden from the public marketplace." : "No payment was made. Your current SaaS access remains unchanged.",
      });
      clearBillingQueryParams();
      return;
    }
    if (billingStatus === "portal") {
      setBillingState({
        tone: "pending",
        title: "Returned from Stripe billing portal",
        body: "Refresh completed. Any Stripe subscription changes will sync back into your dashboard shortly.",
      });
      onSessionRefresh?.();
      loadDashboard().catch(() => {});
      clearBillingQueryParams();
    }
  }, [clearBillingQueryParams, hasPendingWelcomeSignature, loadDashboard, onSessionRefresh, persistDashboardTabQuery, pollCheckoutStatus, tabs]);

  useEffect(() => {
    if (!hasPersistedInitialDashboardTab.current) {
      hasPersistedInitialDashboardTab.current = true;
      return;
    }
    persistDashboardTabQuery(activeTab === "summary" ? "" : activeTab);
  }, [activeTab, persistDashboardTabQuery]);

  useEffect(() => {
    if (hasPendingWelcomeSignature && user?.role === "agent" && user?.subscription_status === "Active") {
      setShowSignatureWelcomeModal(true);
    }
  }, [hasPendingWelcomeSignature, user?.role, user?.subscription_status]);

  useEffect(() => {
    if (activeTab === "users" && canManageUsers) {
      const alignUsersCommandCenter = () => {
        if (!usersTabRef.current) return;
        const stickyBar = usersTabRef.current.querySelector('[data-testid="users-command-bar-sticky"]');
        const anchor = stickyBar || usersTabRef.current;
        const top = anchor.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top: Math.max(top, 0), behavior: "auto" });
      };

      const firstTimer = window.setTimeout(alignUsersCommandCenter, 80);
      const secondTimer = window.setTimeout(alignUsersCommandCenter, 260);
      return () => {
        window.clearTimeout(firstTimer);
        window.clearTimeout(secondTimer);
      };
    }
  }, [activeTab, canManageUsers]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadListings().catch(() => {});
    }, 250);
    return () => clearTimeout(timeout);
  }, [loadListings]);

  useEffect(() => {
    const marketingPollInterval = activeTab === "marketing" && marketingSelectedCampaign?.publishing_result?.status === "publishing_in_progress" ? 4000 : POLLING_INTERVAL;
    const interval = setInterval(() => {
      loadListings().catch(() => {});
      loadDashboardMeta().catch(() => {});
      if (activeTab === "marketing") {
        loadMarketingCampaigns().catch(() => {});
        loadMarketingSocialAccounts().catch(() => {});
        if (marketingSelectedCampaign?.id) {
          loadMarketingTimeline(marketingSelectedCampaign.id).catch(() => {});
        }
      }
    }, marketingPollInterval);
    return () => clearInterval(interval);
  }, [activeTab, loadDashboardMeta, loadListings, loadMarketingCampaigns, loadMarketingSocialAccounts, loadMarketingTimeline, marketingSelectedCampaign?.id, marketingSelectedCampaign?.publishing_result?.status]);

  useEffect(() => {
    if (!editingListingId) return;

    const timeout = window.setTimeout(() => {
      listingManagementFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(timeout);
  }, [editingListingId]);

  const resetListingForm = () => {
    setListingForm(makeListingForm());
    setEditingListingId("");
  };

  const saveListing = async (event) => {
    event.preventDefault();
    if (isPendingAgent) {
      toast.error("Complete Claim & Verify before editing or creating listings.");
      setShowAmbassadorClaimModal(true);
      return;
    }
    if (isReadOnlyAgent) {
      toast.error("Your dashboard is read-only until the subscription is restored.");
      return;
    }
    const payload = makePayload(listingForm);

    try {
      if (editingListingId) {
        const response = await api.patch(`/listings/${editingListingId}`, payload, authConfig());
        toast.success("Listing updated successfully");
        setListingForm(mapListingToForm(response.data));
      } else {
        await api.post("/listings", payload, authConfig());
        toast.success("Listing created successfully");
        resetListingForm();
      }
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const patchListingField = async (listingId, updates, successMessage) => {
    if (isPendingAgent) {
      toast.error("Complete Claim & Verify before modifying this portal.");
      setShowAmbassadorClaimModal(true);
      return;
    }
    if (isReadOnlyAgent) {
      toast.error("Your dashboard is read-only until the subscription is restored.");
      return;
    }
    try {
      const response = await api.patch(`/listings/${listingId}`, updates, authConfig());
      if (editingListingId === listingId) setListingForm(mapListingToForm(response.data));
      toast.success(successMessage);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const removeListing = async (listingId) => {
    if (isPendingAgent) {
      toast.error("Complete Claim & Verify before modifying this portal.");
      setShowAmbassadorClaimModal(true);
      return;
    }
    if (isReadOnlyAgent) {
      toast.error("Your dashboard is read-only until the subscription is restored.");
      return;
    }
    try {
      await api.delete(`/listings/${listingId}`, authConfig());
      toast.success("Listing deleted");
      setSelectedListingIds((current) => current.filter((id) => id !== listingId));
      if (editingListingId === listingId) resetListingForm();
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const syncPropnexPortfolio = async () => {
    if (isPendingAgent) {
      if (user.claim_verified_at) {
        navigate(`/agent/${user.subdomain_slug}/founding-checkout`);
        toast.info("Claim verified. Finish the founding checkout to unlock the live route.");
      } else {
        setShowAmbassadorClaimModal(true);
        toast.info("This Ambassador cockpit is already hydrated. Complete Claim & Verify before requesting a new sync.");
      }
      return;
    }
    if (isAgentView) {
      if (!canUsePropNexImport) {
        openUpgradeModal("propnexImport");
        return;
      }
      setShowAgentImportModal(true);
      return;
    }
    setSyncingPortfolio(true);
    try {
      const response = await api.post("/dashboard/sync-propnex", {}, authConfig());
      setSyncStatus(response.data);
        toast.success(response.data.status === "running" ? "Portfolio sync started. The dashboard will keep polling in the background." : response.data.message);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSyncingPortfolio(false);
    }
  };

  const submitAgentImport = async (event) => {
    event.preventDefault();
    if (!syncConsentChecked) {
      toast.error("Consent is required before the authorised portfolio sync can start.");
      return;
    }
    setSyncingPortfolio(true);
    try {
      const response = await api.post("/dashboard/agent-propnex-sync", { consent: syncConsentChecked }, authConfig());
      setSyncStatus(response.data);
      toast.success(response.data.message || "Portfolio sync started.");
      setShowAgentImportModal(false);
      setSyncConsentChecked(false);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSyncingPortfolio(false);
    }
  };

  const submitPrivateTransactionUpload = async (event) => {
    event.preventDefault();
    if (!privateTransactionFile) {
      toast.error("Upload a CSV or XLSX file with your private transactions.");
      return;
    }
    if (!privateTransactionForm.source_name.trim()) {
      toast.error("Please name this private transaction upload source.");
      return;
    }
    if (!isAgentView && !privateTransactionForm.owner_id) {
      toast.error("Select the target agent portfolio before uploading private transactions.");
      return;
    }

    setPrivateTransactionUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", privateTransactionFile);
      formData.append("source_name", privateTransactionForm.source_name.trim());
      formData.append("notes", privateTransactionForm.notes.trim());
      if (!isAgentView && privateTransactionForm.owner_id) {
        formData.append("owner_id", privateTransactionForm.owner_id);
      }
      const response = await api.post("/dashboard/private-transactions/upload", formData, authConfig());
      setPrivateTransactionSummary(response.data.summary);
      setPrivateTransactionFile(null);
      setPrivateTransactionForm((current) => ({
        ...current,
        notes: "",
        source_name: isAgentView ? current.source_name : "Private Transaction Desk",
      }));
      toast.success(response.data.message);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setPrivateTransactionUploading(false);
    }
  };

  const submitMagicDropUpload = async (event) => {
    event.preventDefault();
    if (!magicDropFile) {
      toast.error("Upload a CSV, Excel, or PDF export for manual import.");
      return;
    }
    setMagicDropUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", magicDropFile);
      const response = await api.post("/agent/magic-drop", formData, authConfig({ headers: { "Content-Type": "multipart/form-data" } }));
      toast.success(`${response.data.synced_count} draft listing${response.data.synced_count === 1 ? "" : "s"} imported with Manual Import.`);
      const reviewListings = response.data.review_listings || [];
      setMagicDropReviewData({
        raw_text: response.data.raw_text || "",
        review_listings: reviewListings,
        source_filename: response.data.source_filename || magicDropFile?.name || "Uploaded export",
      });
      setMagicDropApprovedListingIds(reviewListings.map((item) => item.listing_id).filter(Boolean));
      setShowMagicDropReviewSheet(reviewListings.length > 0);
      setMagicDropFile(null);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Manual import failed"));
    } finally {
      setMagicDropUploading(false);
    }
  };

  const handleMagicDropFiles = (files) => {
    const nextFile = files?.[0] || null;
    setMagicDropFile(nextFile);
    setMagicDropDragActive(false);
  };

  const toggleMagicDropApprovedListing = useCallback((listingId) => {
    setMagicDropApprovedListingIds((current) => (
      current.includes(listingId) ? current.filter((id) => id !== listingId) : [...current, listingId]
    ));
  }, []);

  const openMagicDropListingInManager = useCallback((listingId) => {
    if (!listingId) return;
    setShowMagicDropReviewSheet(false);
    setEditingListingId(listingId);
    setActiveTab("listings");
  }, []);

  const publishApprovedMagicDropListings = useCallback(async () => {
    const approvedIds = magicDropApprovedListingIds.filter(Boolean);
    if (!approvedIds.length) {
      toast.error("Select at least one extracted draft to publish.");
      return;
    }
    setMagicDropPublishing(true);
    try {
      await api.post("/agent/magic-drop/publish", { listing_ids: approvedIds }, authConfig());
      setMagicDropReviewData((current) => {
        if (!current) return current;
        return {
          ...current,
          review_listings: (current.review_listings || []).map((item) => (
            approvedIds.includes(item.listing_id) ? { ...item, status: "Available" } : item
          )),
        };
      });
      toast.success(`${approvedIds.length} imported draft${approvedIds.length === 1 ? "" : "s"} published with historical enrichment.`);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to publish the approved imported drafts"));
    } finally {
      setMagicDropPublishing(false);
    }
  }, [loadDashboard, magicDropApprovedListingIds]);

  const createUser = async (event) => {
    event.preventDefault();
    try {
      await api.post("/users", newUser, authConfig());
      toast.success("New staff user created");
      setNewUser(makeNewUserForm());
      setIsUserDrawerOpen(false);
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const inviteTeamMember = async (event) => {
    event.preventDefault();
    try {
      await api.post("/team/members", teamInvite, authConfig());
      toast.success("Team seat created and invite email sent");
      setTeamInvite({ name: "", email: "", cea_registration_number: "", phone: "", team_member_status: "Active" });
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const updateTeamMemberStatus = async (memberId, team_member_status) => {
    try {
      await api.patch(`/team/members/${memberId}`, { team_member_status }, authConfig());
      toast.success("Team member status updated");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const saveTeamBranding = async (event) => {
    event.preventDefault();
    try {
      await api.patch("/team/branding", teamBranding, authConfig());
      toast.success("Team branding updated");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const assignLead = async (leadId, assignedAgentId) => {
    try {
      await api.post(`/leads/${leadId}/assign`, { assigned_agent_id: assignedAgentId }, authConfig());
      toast.success("Lead assigned to team member");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const saveLeadCompliance = async () => {
    if (!selectedLead) return;
    try {
      const response = await api.patch(`/leads/${selectedLead.id}/compliance`, leadComplianceDraft, authConfig());
      setSelectedLead(response.data);
      toast.success("Compliance checklist saved");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const saveLeadStatus = async () => {
    if (!selectedLead) return;
    setSavingLeadStatus(true);
    try {
      const response = await api.patch(`/dashboard/leads/${selectedLead.id}/status`, leadStatusDraft, authConfig());
      setSelectedLead(response.data);
      setLeads((current) => current.map((lead) => (lead.id === response.data.id ? response.data : lead)));
      toast.success("Lead status updated");
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSavingLeadStatus(false);
    }
  };

  const saveUserDraft = async (userId) => {
    try {
      await api.patch(`/users/${userId}`, userDrafts[userId], authConfig());
      toast.success("User updated successfully");
      setIsUserDrawerOpen(false);
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`, authConfig());
      toast.success("User deleted");
      if (editingUserId === userId) {
        setEditingUserId("");
        setIsUserDrawerOpen(false);
      }
      await loadDashboardMeta();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const openCreateUserDrawer = useCallback(() => {
    setUserDrawerMode("create");
    setEditingUserId("");
    setNewUser(makeNewUserForm());
    setIsUserDrawerOpen(true);
  }, []);

  const openEditUserDrawer = useCallback((userId) => {
    setUserDrawerMode("edit");
    setEditingUserId(userId);
    setIsUserDrawerOpen(true);
  }, []);

  const handleUserDrawerOpenChange = useCallback((nextOpen) => {
    setIsUserDrawerOpen(nextOpen);
    if (!nextOpen && userDrawerMode === "edit") {
      setEditingUserId("");
    }
  }, [userDrawerMode]);

  const updateNewUserField = useCallback((field, value) => {
    setNewUser((current) => ({ ...current, [field]: value }));
  }, []);

  const updateEditingUserField = useCallback((field, value) => {
    if (!editingUser) return;
    setUserDrafts((current) => {
      const currentDraft = current[editingUser.id] || makeUserDraft(editingUser);
      return { ...current, [editingUser.id]: { ...currentDraft, [field]: value } };
    });
  }, [editingUser]);

  const handleUserSortChange = useCallback((nextValue) => {
    const [key, direction] = String(nextValue).split(":");
    setUserSort({ key, direction: direction === "asc" ? "asc" : "desc" });
  }, []);

  const handleUserSortHeaderClick = useCallback((sortKey) => {
    setUserSort((current) => ({
      key: sortKey,
      direction: current.key === sortKey && current.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const requestSignature = async (resourceType = "image", folder = "uploads/listings") => {
    const response = await api.get("/cloudinary/signature", authConfig({ params: { resource_type: resourceType, folder } }));
    return response.data;
  };

  const persistListingAssetFields = async (nextFields, successMessage) => {
    const response = await api.patch(`/listings/${editingListingId}`, nextFields, authConfig());
    setListingForm(mapListingToForm(response.data));
    await loadListings();
    toast.success(successMessage);
  };

  const persistAdminMedia = async (nextMedia, successMessage) => {
    const response = await api.patch(`/listings/${editingListingId}`, { admin_media: nextMedia }, authConfig());
    setListingForm(mapListingToForm(response.data));
    await loadListings();
    toast.success(successMessage);
  };

  const persistUploadedAssets = async (assets) => {
    const currentMedia = listingForm.admin_media || { primary_image: {}, gallery: [] };
    const nextGallery = [...(currentMedia.gallery || []), ...assets];
    const nextMedia = {
      primary_image: currentMedia.primary_image?.public_id ? currentMedia.primary_image : assets[0],
      gallery: nextGallery,
    };
    await persistAdminMedia(nextMedia, "Admin-uploaded media saved and prioritized");
  };

  const uploadListingMedia = async (files) => {
    if (!editingListingId) {
      toast.error("Save the listing first, then upload premium media in Edit mode.");
      return;
    }
    if (!files.length) return;
    setUploading(true);
    try {
      const signaturePayload = await requestSignature();
      const uploads = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", signaturePayload.api_key);
        formData.append("timestamp", String(signaturePayload.timestamp));
        formData.append("signature", signaturePayload.signature);
        formData.append("folder", signaturePayload.folder);
        const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.cloud_name}/image/upload`, {
          method: "POST",
          body: formData,
        });
        const asset = await cloudinaryResponse.json();
        if (!cloudinaryResponse.ok) {
          throw new Error(asset.error?.message || "Cloudinary upload failed");
        }
        uploads.push({
          public_id: asset.public_id,
          secure_url: asset.secure_url,
          optimized_url: optimizeCloudinaryUrl(asset.secure_url, 1800),
          thumb_url: optimizeCloudinaryUrl(asset.secure_url, 480),
          uploaded_at: new Date().toISOString(),
        });
      }
      await persistUploadedAssets(uploads);
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || "Unable to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelection = async (event) => {
    await uploadListingMedia(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const uploadPlanPdfSet = async (files, mode = "floorplan") => {
    if (!editingListingId) {
      toast.error("Save the listing first, then upload plan PDFs in Edit mode.");
      return;
    }
    if (!files.length) return;
    const isSiteplan = mode === "siteplan";
    if (isSiteplan) {
      setUploadingSiteplanPdf(true);
    } else {
      setUploadingFloorplanPdf(true);
    }
    try {
      const signaturePayload = await requestSignature("raw", isSiteplan ? "uploads/listings/siteplans" : "uploads/listings/floorplans");
      const uploadedUrls = [];
      for (const file of files) {
        const normalizedName = String(file.name || "").toLowerCase();
        if (!normalizedName.endsWith(".pdf")) {
          throw new Error("Only PDF floor plan / site plan files are supported here.");
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", signaturePayload.api_key);
        formData.append("timestamp", String(signaturePayload.timestamp));
        formData.append("signature", signaturePayload.signature);
        formData.append("folder", signaturePayload.folder);
        const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.cloud_name}/raw/upload`, {
          method: "POST",
          body: formData,
        });
        const asset = await cloudinaryResponse.json();
        if (!cloudinaryResponse.ok) {
          throw new Error(asset.error?.message || "Cloudinary PDF upload failed");
        }
        uploadedUrls.push(asset.secure_url);
      }
      const existingUrls = isSiteplan ? (listingForm.siteplan_pdf_urls || []) : (listingForm.floorplan_pdf_urls || []);
      const nextPdfUrls = [...new Set([...existingUrls, ...uploadedUrls])];
      await persistListingAssetFields(
        isSiteplan ? { siteplan_pdf_urls: nextPdfUrls } : { floorplan_pdf_urls: nextPdfUrls },
        isSiteplan ? "Site plan PDFs uploaded" : "Floor plan PDFs uploaded",
      );
    } catch (error) {
      toast.error(error.message || error.response?.data?.detail || "Unable to upload plan PDFs");
    } finally {
      if (isSiteplan) {
        setUploadingSiteplanPdf(false);
      } else {
        setUploadingFloorplanPdf(false);
      }
    }
  };

  const handleFloorplanPdfSelection = async (event) => {
    await uploadPlanPdfSet(Array.from(event.target.files || []), "floorplan");
    event.target.value = "";
  };

  const handleSiteplanPdfSelection = async (event) => {
    await uploadPlanPdfSet(Array.from(event.target.files || []), "siteplan");
    event.target.value = "";
  };

  const removePlanPdf = async (targetUrl, mode = "floorplan") => {
    const isSiteplan = mode === "siteplan";
    const nextPdfUrls = (isSiteplan ? (listingForm.siteplan_pdf_urls || []) : (listingForm.floorplan_pdf_urls || [])).filter((item) => item !== targetUrl);
    try {
      await persistListingAssetFields(isSiteplan ? { siteplan_pdf_urls: nextPdfUrls } : { floorplan_pdf_urls: nextPdfUrls }, isSiteplan ? "Site plan PDF removed" : "Floor plan PDF removed");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const removeAdminMedia = async (publicId) => {
    try {
      const response = await api.post(`/listings/${editingListingId}/admin-media/remove`, { public_id: publicId }, authConfig());
      setListingForm(mapListingToForm(response.data));
      await loadListings();
      toast.success("Uploaded media removed");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const reorderAdminMedia = async (draggedPublicId, targetPublicId) => {
    const gallery = listingForm.admin_media?.gallery || [];
    const nextGallery = moveGalleryAsset(gallery, draggedPublicId, targetPublicId);
    if (nextGallery === gallery) return;

    try {
      await persistAdminMedia(
        {
          primary_image: nextGallery[0] || {},
          gallery: nextGallery,
        },
        "Media order updated — hero image priority saved",
      );
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const loadInvestmentForecastRow = useCallback(async (listingId) => {
    if (investmentForecastCache[listingId]) return investmentForecastCache[listingId];
    setLoadingInvestmentRows((current) => (current.includes(listingId) ? current : [...current, listingId]));
    try {
      const response = await api.get(`/dashboard/listings/${listingId}/investment-forecast`, authConfig());
      setInvestmentForecastCache((current) => ({ ...current, [listingId]: response.data }));
      return response.data;
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load AI investment logic"));
      return null;
    } finally {
      setLoadingInvestmentRows((current) => current.filter((id) => id !== listingId));
    }
  }, [investmentForecastCache]);

  const toggleInvestmentRow = async (listingId) => {
    if (expandedInvestmentRows.includes(listingId)) {
      setExpandedInvestmentRows((current) => current.filter((id) => id !== listingId));
      return;
    }
    await loadInvestmentForecastRow(listingId);
    setExpandedInvestmentRows((current) => [...current, listingId]);
  };

  const exportUnderValuedCsv = async () => {
    try {
      const response = await api.get("/dashboard/listings/export-under-valued.csv", authConfig({
        params: {
          search: listingFilters.search || undefined,
          district: listingFilters.district || undefined,
          status: listingFilters.status || undefined,
          min_price: listingFilters.min_price ? Number(listingFilters.min_price) : undefined,
          max_price: listingFilters.max_price ? Number(listingFilters.max_price) : undefined,
        },
        responseType: "blob",
      }));
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = "sg-prop-portal-under-valued-weekly.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Weekly under-valued export downloaded");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to export under-valued listings"));
    }
  };

  const setPrimaryAdminMedia = async (publicId) => {
    const gallery = listingForm.admin_media?.gallery || [];
    const targetAsset = gallery.find((asset) => asset.public_id === publicId);
    if (!targetAsset) return;

    const nextGallery = [targetAsset, ...gallery.filter((asset) => asset.public_id !== publicId)];

    try {
      await persistAdminMedia(
        {
          primary_image: targetAsset,
          gallery: nextGallery,
        },
        "Hero image updated successfully",
      );
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const toggleListingSelection = (listingId) => {
    setSelectedListingIds((current) => (current.includes(listingId) ? current.filter((id) => id !== listingId) : [...current, listingId]));
  };

  const toggleSelectAllListings = () => {
    if (selectedListingIds.length === listings.length) {
      setSelectedListingIds([]);
      return;
    }
    setSelectedListingIds(listings.map((listing) => listing.id));
  };

  const runBulkAction = async (action) => {
    if (isPendingAgent) {
      toast.error("Bulk listing changes are locked until Claim & Verify is completed.");
      setShowAmbassadorClaimModal(true);
      return;
    }
    if (isReadOnlyAgent) {
      toast.error("Your dashboard is read-only until the subscription is restored.");
      return;
    }
    if (!selectedListingIds.length) {
      toast.error("Select at least one listing first");
      return;
    }
    try {
      await api.post(
        "/listings/bulk-action",
        {
          action,
          listing_ids: selectedListingIds,
          status_value: action === "status" ? bulkStatusValue : undefined,
        },
        authConfig(),
      );
      toast.success("Bulk action completed");
      setSelectedListingIds([]);
      await loadDashboard();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const filteredLeads = useMemo(() => {
    let nextLeads = [...leads];
    if (leadSourceFilter !== "all") {
      nextLeads = nextLeads.filter((lead) => (lead.lead_source || lead.source) === leadSourceFilter);
    }
    if (leadStatusFilter !== "all") {
      nextLeads = nextLeads.filter((lead) => (lead.lead_status || "new") === leadStatusFilter);
    }
    if (!canUseSalesCommandCenter && isAgentView) return nextLeads;
    if (leadQuickFilter === "hot") return nextLeads.filter((lead) => Number(lead.lead_intensity_score || 0) > 70);
    if (leadQuickFilter === "foreigner") return nextLeads.filter((lead) => lead.is_foreigner_profile);
    if (leadQuickFilter === "pdf_heavy") return nextLeads.filter((lead) => Number(lead.pdf_download_count || 0) >= 2);
    if (leadQuickFilter === "prime") return nextLeads.filter((lead) => lead.is_prime_district_focused);
    if (leadQuickFilter === "d15") return nextLeads.filter((lead) => lead.is_d15_focused);
    return nextLeads;
  }, [canUseSalesCommandCenter, isAgentView, leadQuickFilter, leadSourceFilter, leadStatusFilter, leads]);

  const buildLeadWhatsAppHref = useCallback((lead) => {
    const digits = String(lead?.phone || "").replace(/\D/g, "");
    if (!digits) return "#";
    return `https://wa.me/${digits}?text=${encodeURIComponent(`Hello ${lead?.name || "there"}, thanks for your enquiry.`)}`;
  }, []);

  const buildLeadEmailHref = useCallback(
    (lead) => `mailto:${lead?.email || ""}?subject=${encodeURIComponent(`Follow-up on your SG Prop Portal enquiry`)}`,
    [],
  );

  const filteredUsers = useMemo(() => {
    const normalizedSearchTerm = userSearchTerm.trim().toLowerCase();
    return users
      .filter((member) => {
        const planMatches = userPlanFilter === "all" || member.plan_type === userPlanFilter;
        if (!planMatches) return false;
        if (!normalizedSearchTerm) return true;
        return [member.name, member.email]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearchTerm));
      })
      .sort((left, right) => {
        if (userSort.key === "created_at") {
          const leftTime = Date.parse(left.created_at || "") || 0;
          const rightTime = Date.parse(right.created_at || "") || 0;
          return userSort.direction === "asc" ? leftTime - rightTime : rightTime - leftTime;
        }
        const leftValue = String(left[userSort.key] || "").toLowerCase();
        const rightValue = String(right[userSort.key] || "").toLowerCase();
        const comparison = leftValue.localeCompare(rightValue);
        return userSort.direction === "asc" ? comparison : -comparison;
      });
  }, [userPlanFilter, userSearchTerm, userSort, users]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 py-10 text-[#001935] md:px-10 lg:px-12">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className={panelClass}>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="admin-dashboard-eyebrow">
                {isAgentView ? "Agent Workspace" : "Admin Control Room"}
              </p>
              <h1 className="mt-4 text-4xl font-semibold text-[#001935] md:text-5xl" data-testid="admin-dashboard-heading">
                Welcome back, {user.name}
              </h1>
              <p className="mt-4 text-base leading-8 text-[#42566f]">
                {isAgentView
                  ? "Manage only your private portfolio, follow up on your listing-driven leads, and keep client delivery focused."
                  : "Professional listing control, compliant portfolio sync visibility, and premium media override tools in one place."}
              </p>
              {isAgentView ? (
                <div className="mt-4 flex flex-wrap gap-3" data-testid="agent-dashboard-profile-strip">
                  <p className="rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-sm font-medium text-[#8b6d16]" data-testid="agent-dashboard-plan-pill">
                    {user.plan_type || "Digital Agent"} • {user.subscription_status || "Trial"}
                  </p>
                  <p className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-sm font-medium text-[#42566f]" data-testid="agent-dashboard-cea-pill">
                    CEA: {user.cea_registration_number || "Pending"}
                  </p>
                  <p className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-sm font-medium text-[#42566f]" data-testid="agent-dashboard-subdomain-pill">
                    Profile Path:{" "}
                    {cleanAgentSubdomainSlug ? (
                      <a className="font-semibold text-[#001935] underline decoration-[#D4AF37] underline-offset-4 transition hover:text-[#8b6d16]" data-testid="agent-dashboard-subdomain-link" href={publicSiteHref} rel="noreferrer" target="_blank">
                        {publicSiteDisplay}
                      </a>
                    ) : (
                      "Not set"
                    )}
                  </p>
                </div>
              ) : (
                <>
                  <p className="mt-4 rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-sm font-medium text-[#8b6d16]" data-testid="admin-dashboard-sync-status-text">
                    {syncStatus.message}
                  </p>
                  <div className="mt-4 rounded-[24px] border border-[#D4AF37]/20 bg-[#fffdf7] px-5 py-4 text-sm leading-7 text-[#6b6252]" data-testid="admin-dashboard-sync-guardrail-info-banner">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Price Guardrails Active</p>
                    <p className="mt-2">
                      Manual price edits are locked, placeholder sync prices cannot overwrite valid values, and any batch with more than 10% invalid pricing will raise a warning before prices are affected.
                    </p>
                  </div>
                  {syncStatus.warnings?.length ? (
                    <div className="mt-4 rounded-[24px] border border-[#D4AF37]/45 bg-[#fff3d8] px-5 py-4 text-sm leading-7 text-[#5c4921]" data-testid="admin-dashboard-sync-warning-banner">
                      <div className="flex items-start gap-3">
                        <ShieldAlert className="mt-0.5 h-5 w-5 text-[#8b6d16]" />
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Sync Warning</p>
                          {syncStatus.warnings.map((warning, index) => (
                            <p className="mt-2" data-testid={`admin-dashboard-sync-warning-${index + 1}`} key={`${warning}-${index}`}>
                              {warning}
                            </p>
                          ))}
                          {syncStatus.price_guardrail?.invalid_price_count ? (
                            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#8b6d16]" data-testid="admin-dashboard-sync-warning-stats">
                              Invalid incoming prices: {syncStatus.price_guardrail.invalid_price_count} / {syncStatus.price_guardrail.total_records}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
              {isPendingAgent && !user.claim_verified_at ? (
                <AmbassadorComplianceBar foundingMembership={summary.founding_membership} onOpenClaimModal={() => setShowAmbassadorClaimModal(true)} pendingCount={pendingAttestationCount} publicPath={publicSiteDisplay} />
              ) : null}
              {isPendingAgent && user.claim_verified_at ? (
                <div className="mt-5 rounded-[26px] border border-[#D4AF37]/22 bg-[#fffaf1] px-5 py-4 text-[#3f3420] shadow-[0_18px_40px_rgba(139,110,61,0.08)]" data-testid="ambassador-checkout-ready-bar">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B6E3D]">Private Showroom ready • Publish when you’re ready</p>
                  <p className="mt-2 text-sm leading-7">Your attestation is saved. Secure your Founding 18 spot to switch this boutique URL public, push your listings into the marketplace, and activate lead tracking.</p>
                </div>
              ) : null}
              {isAgentView && canViewSyncStatus ? (
                <div className="mt-4 rounded-[24px] border border-[#D4AF37]/28 bg-[#fffaf1] px-5 py-4 text-sm leading-7 text-[#3f3420]" data-testid="agent-dashboard-sync-status-banner">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Portfolio Sync Status</p>
                  <p className="mt-2 font-semibold text-[#2c2c2c]" data-testid="agent-dashboard-sync-status-text">{syncStatus.message}</p>
                  {syncStatus.warnings?.length ? (
                    <div className="mt-3 space-y-2 text-xs leading-6 text-[#5c4921]" data-testid="agent-dashboard-sync-warning-list">
                      {syncStatus.warnings.map((warning, index) => (
                        <p data-testid={`agent-dashboard-sync-warning-${index + 1}`} key={`${warning}-${index}`}>
                          {warning}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {isReadOnlyAgent ? (
                <div className="mt-4 rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-sm leading-7 text-red-700" data-testid="agent-read-only-banner">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-600">Read-only dashboard</p>
                  <p className="mt-2">Your subscription is currently {user.subscription_status}. Restore Subscription to create, edit, sync, or generate new outputs.</p>
                  <button className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="agent-restore-subscription-button" disabled={Boolean(billingBusyKey)} onClick={() => launchPlanAction(getPackageIdForPlanType(user.plan_type), { preferPortal: true, portalMessage: "Opening Stripe to restore this subscription." })} type="button">
                    Restore Subscription
                  </button>
                </div>
              ) : null}
              {isAgentView && billingState.tone !== "idle" ? (
                <div className={`mt-4 rounded-[24px] px-5 py-4 text-sm leading-7 ${billingState.tone === "success" ? "border border-emerald-200 bg-emerald-50 text-emerald-800" : billingState.tone === "error" ? "border border-red-200 bg-red-50 text-red-700" : billingState.tone === "warning" ? "border border-amber-200 bg-amber-50 text-amber-800" : "border border-[#D4AF37]/25 bg-[#fffaf1] text-[#5c4921]"}` } data-testid="agent-billing-status-banner">
                  <p className="text-xs font-bold uppercase tracking-[0.18em]" data-testid="agent-billing-status-title">{billingState.title}</p>
                  <p className="mt-2" data-testid="agent-billing-status-body">{billingState.body}</p>
                </div>
              ) : null}
              {isAgentView && user.subscription_status === "Active" ? (
                <div className="mt-4 rounded-[24px] border border-[#D4AF37]/28 bg-[#fffaf1] px-5 py-4 text-sm leading-7 text-[#3f3420]" data-testid="agent-dashboard-manual-guidance-banner">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Post-subscription portal guide</p>
                  <p className="mt-2">Use the Agent Manual to follow the complete journey after purchase: onboarding wizard, dashboard, public portal pages, portfolio sync, listings, leads, marketing, network, create listing, billing, and final Public Site review.</p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link className="gold-button !px-5 !py-3" data-testid="agent-dashboard-open-full-manual-link" to="/agent-manual">
                      <BookOpenCheck className="mr-2 h-4 w-4" /> Open Full Agent Manual
                    </Link>
                    <a className="outline-button" data-testid="agent-dashboard-open-public-site-from-manual-banner" href={publicSiteHref} rel="noreferrer" target="_blank">
                      <ExternalLink className="mr-2 h-4 w-4" /> Open Public Site
                    </a>
                  </div>
                </div>
              ) : null}
            {isAgentView && user.role === "agent" ? (
              <div className="mt-4 rounded-[24px] border border-[#D4AF37]/28 bg-[linear-gradient(135deg,#fffaf1_0%,#fff7e7_100%)] px-5 py-5 text-sm leading-7 text-[#3f3420] shadow-[0_20px_45px_rgba(139,110,61,0.08)]" data-testid="agent-dashboard-magic-drop-panel">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Manual Import • AI Assist</p>
                <h3 className="mt-2 text-2xl font-semibold text-[#001935]">Manual Import: Upload your listing export and let AI structure it</h3>
                <p className="mt-2 text-sm leading-7 text-[#5c4921]">Upload your listing export (CSV, Excel, PDF) and our AI will format it automatically. Imported listings are saved as <strong>Draft</strong> for review before publishing.</p>
                <form className="mt-5 grid gap-4" data-testid="agent-dashboard-magic-drop-form" onSubmit={submitMagicDropUpload}>
                  <label
                    className={`group relative block cursor-pointer overflow-hidden rounded-[28px] border border-dashed px-6 py-8 text-center transition ${magicDropDragActive ? "border-[#D4AF37] bg-[#fff8ea] shadow-[0_22px_46px_rgba(212,175,55,0.14)]" : "border-[#D4AF37]/45 bg-white/80 hover:border-[#D4AF37] hover:bg-[#fffdf7]"}`}
                    data-testid="agent-dashboard-magic-drop-dropzone"
                    htmlFor="agent-dashboard-magic-drop-input"
                    onDragEnter={(event) => {
                      event.preventDefault();
                      setMagicDropDragActive(true);
                    }}
                    onDragLeave={(event) => {
                      event.preventDefault();
                      const nextTarget = event.relatedTarget;
                      if (nextTarget && event.currentTarget.contains(nextTarget)) return;
                      setMagicDropDragActive(false);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setMagicDropDragActive(true);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      handleMagicDropFiles(event.dataTransfer?.files);
                    }}
                  >
                    <input
                      accept=".csv,.xlsx,.xlsm,.pdf"
                      className="hidden"
                      data-testid="agent-dashboard-magic-drop-input"
                      id="agent-dashboard-magic-drop-input"
                      onChange={(event) => handleMagicDropFiles(event.target.files)}
                      type="file"
                    />
                    <div className="mx-auto flex max-w-2xl flex-col items-center justify-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fff4dd] text-[#8b6d16] shadow-[0_12px_26px_rgba(212,175,55,0.18)]">
                        {magicDropUploading ? <LoaderCircle className="h-7 w-7 animate-spin" data-testid="agent-dashboard-magic-drop-loading-icon" /> : <UploadCloud className="h-7 w-7" />}
                      </div>
                      <p className="mt-5 text-base font-semibold text-[#001935]" data-testid="agent-dashboard-magic-drop-heading">Manual Import: Upload your listing export (CSV, Excel, PDF) and our AI will format it automatically.</p>
                      <p className="mt-2 text-sm leading-7 text-[#6b6252]" data-testid="agent-dashboard-magic-drop-helper-copy">Drag a file here or click to browse. Best for messy exports, mixed columns, and PDF portfolio dumps.</p>
                      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#001935]/10 bg-[#fcfaf5] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="agent-dashboard-magic-drop-file-pill">
                        <FileStack className="h-4 w-4 text-[#8b6d16]" />
                        {magicDropFile ? magicDropFile.name : "No file selected yet"}
                      </div>
                    </div>
                  </label>
                  <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4 text-xs leading-6 text-[#5f5a4e]" data-testid="agent-dashboard-magic-drop-compliance-note">
                    AI will extract only the core fields needed for draft listing creation: title, property type, price, bedrooms, bathrooms, size in sqft, and description. Review each draft manually before publishing to stay compliant.
                  </div>
                  <button className="gold-button w-full" data-testid="agent-dashboard-magic-drop-submit-button" disabled={!magicDropFile || magicDropUploading || isReadOnlyAgent} type="submit">
                    {magicDropUploading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                    {magicDropUploading ? "AI is structuring your export..." : "Run Manual Import"}
                  </button>
                  {magicDropReviewData?.review_listings?.length ? (
                    <button className="outline-button w-full" data-testid="agent-dashboard-magic-drop-review-button" onClick={() => setShowMagicDropReviewSheet(true)} type="button">
                      <Eye className="mr-2 h-4 w-4" /> Review latest import before publishing
                    </button>
                  ) : null}
                </form>
              </div>
            ) : null}
              {needsProfileSetupPrompt ? (
                <div className="mt-4 rounded-[24px] border border-red-200 bg-red-50 px-5 py-4 text-sm leading-7 text-red-700" data-testid="agent-dashboard-profile-compliance-banner">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-600">Complete your profile settings</p>
                  <p className="mt-2">Before your portal is fully compliant, please add: {missingComplianceLabels.join(", ")}.</p>
                  <button className="mt-4 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="agent-dashboard-open-profile-settings-banner-button" onClick={() => setShowAgentProfileSettingsSheet(true)} type="button">
                    Open Profile Settings
                  </button>
                </div>
              ) : null}
            </div>
            {isAgentView ? (
              <button className="inline-flex items-center gap-3 rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-sm font-semibold text-[#001935] shadow-[0_10px_24px_rgba(0,25,53,0.06)]" data-testid="agent-dashboard-open-profile-settings-button" onClick={() => setShowAgentProfileSettingsSheet(true)} type="button">
                {user.profile_photo_url ? <img alt={`${user.name || "Agent"} avatar`} className="h-9 w-9 rounded-full object-cover" data-testid="agent-dashboard-profile-settings-avatar" src={user.profile_photo_url} /> : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#fff4dd] text-[#8b6d16]"><UserCog className="h-4 w-4" /></span>}
                <span className="text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Profile</span>
                  <span className="block">Settings</span>
                </span>
              </button>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <a className="outline-button" data-testid="admin-dashboard-public-site-link" href={publicSiteHref} rel="noreferrer" target="_blank">
                <ExternalLink className="mr-2 h-4 w-4" /> {isPrivateShowroom ? "Open Private Showroom" : "Public Site"}
              </a>
              <button className="outline-button" data-testid="admin-dashboard-refresh-button" onClick={loadDashboard} type="button">
                <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
              </button>
              {canAccessInternalOps ? (
                <button className="gold-button" data-testid="admin-dashboard-sync-propnex-button" disabled={syncingPortfolio} onClick={syncPropnexPortfolio} type="button">
                  <RefreshCcw className="mr-2 h-4 w-4" /> {syncingPortfolio ? "Syncing Portfolio..." : "Sync Portfolio"}
                </button>
              ) : isPendingAgent ? (
                <button className="gold-button" data-testid="ambassador-claim-verify-header-button" onClick={() => (user.claim_verified_at ? navigate(`/agent/${user.subdomain_slug}/founding-checkout`) : setShowAmbassadorClaimModal(true))} type="button">
                  <ShieldAlert className="mr-2 h-4 w-4" /> {user.claim_verified_at ? "Secure My Founding Spot & Go Live" : "Claim & Verify"}
                </button>
              ) : isAgentView ? (
                <button className="gold-button" data-testid="agent-dashboard-propnex-import-button" disabled={syncingPortfolio || isReadOnlyAgent} onClick={syncPropnexPortfolio} type="button">
                  <RefreshCcw className="mr-2 h-4 w-4" /> {syncingPortfolio ? "Starting Sync..." : "Portfolio Sync"}
                </button>
              ) : null}
              {user.role === "agent" ? (
                <button className="outline-button" data-testid="agent-manage-billing-button" disabled={Boolean(billingBusyKey)} onClick={() => openBillingPortal()} type="button">
                  {billingBusyKey === "portal" ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Manage Billing
                </button>
              ) : null}
              <button className="outline-button" data-testid="admin-dashboard-logout-button" onClick={onLogout} type="button">
                <LogOut className="mr-2 h-4 w-4" /> Sign Out
              </button>
            </div>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-4">
            {Object.entries(summary.counts).map(([key, value]) => (
              <AdminStatCard key={key} label={key.replace(/_/g, " ")} testId={`dashboard-count-${key}`} value={value} />
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {tabs.map((tab) => (
            <button
              className={`rounded-full px-5 py-3 text-sm font-semibold transition ${activeTab === tab.key ? "bg-[#D4AF37] text-[#001935] shadow-sm" : "border border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/50 hover:text-[#001935]"}`}
              data-testid={`dashboard-tab-${tab.key}`}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && activeTab !== "marketing" ? <div className={panelClass} data-testid="admin-dashboard-loading-state">{isAgentView ? <AgentDashboardSkeleton /> : "Loading dashboard data..."}</div> : null}

        {!loading && activeTab === "summary" ? (
          <div className="grid gap-6 lg:grid-cols-2">
            {showAmbassadorFoundingPanels ? (
              <>
                <div className="lg:col-span-2">
                  <AmbassadorFoundingHud foundingMembership={summary.founding_membership} pendingCount={pendingAttestationCount} publicPath={publicSiteDisplay} user={user} />
                </div>
                <div className="lg:col-span-2">
                  <AmbassadorSeatLedger foundingMembership={summary.founding_membership} />
                </div>
                <div className="lg:col-span-2">
                  <AmbassadorAlphaCockpit listings={listings} />
                </div>
                <div className="lg:col-span-2">
                  <AmbassadorLeadCommandCenter listings={listings} />
                </div>
              </>
            ) : null}

            {!showAmbassadorFoundingPanels && showFoundingSeatLedger ? (
              <div className="lg:col-span-2">
                <AmbassadorSeatLedger foundingMembership={summary.founding_membership} />
              </div>
            ) : null}
            {canAccessInternalOps && summary.ambassador_rollout_statuses?.length ? (
              <div className="lg:col-span-2">
                <AmbassadorRolloutMonitor ambassadors={summary.ambassador_rollout_statuses} />
              </div>
            ) : null}
            {canAccessInternalOps ? (
              <div className="lg:col-span-3">
                <ActivationAuditTable bypassCandidates={activationBypassCandidates} bypassSubmitting={adminBypassSubmitting} onAdminBypass={runAdminActivationBypass} rows={summary.activation_audit_entries || []} />
              </div>
            ) : null}
            {canAccessInternalOps ? (
              <div className="lg:col-span-3">
                <AdminBillingTelemetryPanel overview={summary.payment_environment_summary} transactions={summary.recent_payment_transactions || []} />
              </div>
            ) : null}
            {summary.analytics ? (
              <div className="lg:col-span-2">
                <AdminAnalyticsExpansionPanel analytics={summary.analytics} cleanupBusy={cleanupBusy} cleanupResult={cleanupResult} isAdmin={user.role === "admin"} onRunCleanup={runAdminCleanup} />
              </div>
            ) : null}
            <div className={panelClass} data-testid="dashboard-recent-listings-card">
              <h2 className="text-3xl font-semibold text-[#001935]">Recent listings</h2>
              <div className="mt-6 grid gap-4">
                {summary.recent_listings.map((listing) => (
                  <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`dashboard-recent-listing-${listing.id}`} key={listing.id}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-lg font-semibold text-[#001935]">{listing.title}</p>
                        <p className="mt-1 text-sm text-[#42566f]">{listing.district} • {formatCategoryLabel(listing.category)}</p>
                      </div>
                      <p className="text-sm font-semibold text-[#8b6d16]" data-testid={`dashboard-recent-listing-price-${listing.id}`}>
                        {formatListingPrice(listing)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={panelClass} data-testid="dashboard-recent-leads-card">
              <h2 className="text-3xl font-semibold text-[#001935]">Recent leads</h2>
              <div className="mt-6 grid gap-4">
                {(summary.recent_leads || []).length ? (
                  summary.recent_leads.map((lead) => (
                    <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`dashboard-recent-lead-${lead.id}`} key={lead.id}>
                      <p className="text-lg font-semibold text-[#001935]">{lead.name}</p>
                      <p className="mt-1 text-sm text-[#42566f]">{lead.email} • {lead.phone}</p>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{lead.source}</p>
                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`dashboard-recent-lead-actions-${lead.id}`}>
                        <a className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1A1A1A]" data-testid={`dashboard-recent-lead-whatsapp-${lead.id}`} href={buildLeadWhatsAppHref(lead)} rel="noreferrer" target="_blank">
                          <MessageCircle className="h-3.5 w-3.5" /> Quick-Action WhatsApp
                        </a>
                        <a className="inline-flex items-center justify-center gap-2 rounded-full border border-[#001935]/12 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#001935]" data-testid={`dashboard-recent-lead-email-${lead.id}`} href={buildLeadEmailHref(lead)}>
                          <Mail className="h-3.5 w-3.5" /> Email Trigger
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 text-sm text-[#42566f]">Lead activity will appear here once brochure or contact forms are used.</div>
                )}
              </div>
            </div>

            {canUsePrivateTransactions ? (
              <div className={`${panelClass} lg:col-span-2`} data-testid="dashboard-private-transactions-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Private Benchmark Inputs</p>
                    <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Upload private transactions to sharpen Alpha Scores</h2>
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-[#42566f]">
                      Upload CSV or XLSX files with district, project, transacted price, sqft, date, tenure, property type, floor, and unit mix. These rows blend into the benchmark engine, refresh price gaps, and tighten district intelligence beyond public data alone.
                    </p>
                  </div>
                  <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] px-4 py-3 text-sm text-[#5c4921]" data-testid="dashboard-private-transactions-scope-pill">
                    {privateTransactionSummary.owner_name || privateTransactionSummary.scope_label || (isAgentView ? user.name : "All portfolios")}
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  <AdminStatCard label="Private rows" testId="dashboard-private-transactions-total-records" value={privateTransactionSummary.total_records || 0} />
                  <AdminStatCard label="Districts covered" testId="dashboard-private-transactions-district-count" value={privateTransactionSummary.district_count || 0} />
                  <AdminStatCard label="Projects covered" testId="dashboard-private-transactions-project-count" value={privateTransactionSummary.project_count || 0} />
                  <div className="rounded-[26px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="dashboard-private-transactions-last-upload-card">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Last upload</p>
                    <p className="mt-3 text-lg font-semibold text-[#001935]">{privateTransactionSummary.last_upload_at ? formatDate(privateTransactionSummary.last_upload_at) : "No uploads yet"}</p>
                  </div>
                </div>

                <div className="mt-6 grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
                  <form className="grid gap-4 rounded-[28px] border border-[#001935]/10 bg-[#fcfaf5] p-5" data-testid="dashboard-private-transactions-upload-form" onSubmit={submitPrivateTransactionUpload}>
                    <div className="grid gap-4 md:grid-cols-2">
                      {!isAgentView ? (
                        <select className={dashboardField} data-testid="dashboard-private-transactions-owner-select" onChange={(event) => setPrivateTransactionForm((current) => ({ ...current, owner_id: event.target.value }))} value={privateTransactionForm.owner_id}>
                          <option value="">Select agent portfolio</option>
                          {ownerOptions.map((member) => (
                            <option key={member.id} value={member.id}>{`${member.name} · /agent/${sanitizeProfilePathSlug(member.subdomain_slug) || "profile"}`}</option>
                          ))}
                        </select>
                      ) : null}
                      <input className={dashboardField} data-testid="dashboard-private-transactions-source-name-input" onChange={(event) => setPrivateTransactionForm((current) => ({ ...current, source_name: event.target.value }))} placeholder="Source name" value={privateTransactionForm.source_name} />
                    </div>
                    <textarea className={`${dashboardField} min-h-28`} data-testid="dashboard-private-transactions-notes-input" onChange={(event) => setPrivateTransactionForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Dataset notes, broker remarks, or underwriting context" value={privateTransactionForm.notes} />
                    <label className="rounded-[24px] border border-dashed border-[#D4AF37]/40 bg-white px-4 py-4 text-sm text-[#42566f]" data-testid="dashboard-private-transactions-file-label">
                      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Upload CSV or XLSX</span>
                      <input accept=".csv,.xlsx" data-testid="dashboard-private-transactions-file-input" onChange={(event) => setPrivateTransactionFile(event.target.files?.[0] || null)} type="file" />
                      <p className="mt-3 text-xs leading-6 text-[#6b6252]">Minimum columns: district, project_name, transacted_price, sqft, transaction_date, tenure, property_type, floor, unit_mix.</p>
                    </label>
                    <div className="flex flex-wrap gap-2" data-testid="dashboard-private-transactions-field-chip-list">
                      {["district", "project_name", "transacted_price", "sqft", "transaction_date", "tenure", "property_type", "floor", "unit_mix"].map((field) => (
                        <span className="rounded-full border border-[#D4AF37]/20 bg-[#fffaf1] px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#8b6d16]" data-testid={`dashboard-private-transactions-field-chip-${field}`} key={field}>
                          {field}
                        </span>
                      ))}
                    </div>
                    <button className="gold-button w-full" data-testid="dashboard-private-transactions-submit-button" disabled={privateTransactionUploading || isReadOnlyAgent} type="submit">
                      {privateTransactionUploading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
                      {privateTransactionUploading ? "Uploading Private Transactions..." : "Upload Private Transactions"}
                    </button>
                  </form>

                  <div className="grid gap-4 rounded-[28px] border border-[#001935]/10 bg-[#fcfaf5] p-5" data-testid="dashboard-private-transactions-recent-panel">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Recent uploads</p>
                      <p className="mt-2 text-sm leading-7 text-[#42566f]">The latest private benchmark drops applied to this portfolio.</p>
                    </div>
                    {(privateTransactionSummary.recent_uploads || []).length ? (
                      privateTransactionSummary.recent_uploads.map((upload) => (
                        <div className="rounded-[22px] border border-[#D4AF37]/20 bg-white px-4 py-4" data-testid={`dashboard-private-transactions-upload-${upload.upload_batch_id}`} key={upload.upload_batch_id}>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-lg font-semibold text-[#001935]">{upload.source_name || upload.source_filename}</p>
                              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{upload.row_count} rows · {upload.source_filename}</p>
                            </div>
                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6b6252]">{upload.uploaded_at ? formatDate(upload.uploaded_at) : "Just now"}</p>
                          </div>
                          <p className="mt-3 text-sm leading-7 text-[#42566f]">Districts: {(upload.districts || []).join(", ") || "—"}</p>
                        </div>
                      ))
                    ) : (
                      <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4 text-sm text-[#42566f]" data-testid="dashboard-private-transactions-empty-state">
                        No private benchmark uploads yet. Once you upload CSV or XLSX transaction rows here, the Alpha Formula will refresh automatically.
                      </div>
                    )}
                    <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="dashboard-private-transactions-district-coverage">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Current district coverage</p>
                      <p className="mt-2">{(privateTransactionSummary.districts_covered || []).length ? privateTransactionSummary.districts_covered.join(", ") : "No districts uploaded yet."}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {!loading && activeTab === "launch-checklist" ? <AdminLaunchChecklistPanel checklist={launchReadiness} onEnvironmentModeChange={handleEnvironmentModeChange} onSendPreview={sendSignaturePreview} sendingPreviewAgentId={sendingSignaturePreviewAgentId} /> : null}

        {!loading && activeTab === "market-insights" ? <AdminMarketInsightsPanel /> : null}

        {activeTab === "marketing" ? (
          <MarketingStudioWorkspace
            campaigns={marketingCampaigns}
            campaignDraft={makeMarketingCampaignTemplate()}
            listings={isAgentView ? listings.filter((listing) => listing.owner_id === user.id) : listings}
            loading={marketingLoading}
            onConnectSocialAccount={connectMarketingSocialAccount}
            onApproveAndSchedule={approveAndScheduleMarketingCampaign}
            onCreateCampaign={createMarketingCampaign}
            onEditCampaignField={editMarketingCampaignField}
            onSelectCampaign={setMarketingSelectedCampaign}
            onSocialAccountDraftChange={editMarketingSocialAccountDraft}
            posting={marketingPosting}
            roleLabel={isAgentView ? "Agent_Personal" : "Platform_Official"}
            selectedCampaign={marketingSelectedCampaign}
            selectedListingId={marketingSelectedListingId}
            setSelectedListingId={setMarketingSelectedListingId}
            socialAccountDrafts={marketingSocialAccountDrafts}
            socialAccounts={marketingSocialAccounts}
            socialAccountsLoading={marketingSocialAccountsLoading}
            socialConnectingPlatform={marketingConnectingPlatform}
            timelineEvents={marketingTimeline}
            timelineLoading={marketingTimelineLoading}
            updating={marketingUpdating}
          />
        ) : null}

        {!loading && activeTab === "create-listing" ? (
          <SovereignListingStudio
            currentUser={user}
            initialListingId={sovereignStudioListingId}
            isAgentView={isAgentView}
            onClearEditTarget={() => setSovereignStudioListingId("")}
            onSaved={() => {
              loadDashboard().catch(() => {});
              loadListings().catch(() => {});
            }}
            ownerOptions={ownerOptions}
          />
        ) : null}

        {!loading && activeTab === "network" && isAgentView && user.role === "agent" ? (
          <AgentNetworkWorkspace
            connectingAgentId={connectingAgentId}
            dealForm={networkDealForm}
            deals={networkDeals}
            directoryAgents={networkDirectory}
            directoryFilters={networkFilters}
            directoryLoading={networkLoading}
            earningsSummary={networkSummary}
            incomingRequests={networkSummary.incoming_requests || []}
            onAcceptRequest={acceptNetworkRequest}
            onChangeDealField={(key, value) => setNetworkDealForm((current) => ({ ...current, [key]: value }))}
            onChangeFilter={updateNetworkFilter}
            onConnect={connectNetworkAgent}
            onPostDeal={postNetworkDeal}
            onRefreshDirectory={() => loadNetworkWorkspace()}
            postingDeal={postingNetworkDeal}
            requestsBusyId={requestsBusyId}
            user={user}
          />
        ) : null}

        {!loading && activeTab === "listings" ? (
          <div className={`grid gap-6 ${editingListingId ? "xl:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)] xl:items-start" : ""}`}>
            {editingListingId ? (
            <form className={`${panelClass} sticky top-28 grid gap-4 self-start xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto`} data-testid="listing-management-form" noValidate onSubmit={saveListing} ref={listingManagementFormRef}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Listing Manager</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">{editingListingId ? "Edit listing" : "Create listing"}</h2>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="listing-sync-helper-text">
                    {isAgentView
                      ? "Create and maintain only your own portfolio listings, with private ownership automatically attached in the backend."
                      : "Edit synced listings, override premium images, hide projects, or publish manual inventory — all without breaking the live site."}
                  </p>
                  <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-[#8b6d16]" data-testid="listing-edit-optional-fields-helper-text">
                    Optional media links can stay blank — admin image uploads save independently.
                  </p>
                </div>
                <button className="outline-button" data-testid="listing-form-reset-button" onClick={resetListingForm} type="button">
                  Reset
                </button>
              </div>

              <input className={dashboardField} data-testid="listing-form-title-input" onChange={(event) => setListingForm({ ...listingForm, title: event.target.value })} placeholder="Listing title" value={listingForm.title} />
              <div className="grid gap-4 sm:grid-cols-2">
                <select className={dashboardField} data-testid="listing-form-category-select" onChange={(event) => setListingForm({ ...listingForm, category: event.target.value })} value={listingForm.category}>
                  <option value="buy">Buy</option>
                  <option value="rent">Rent</option>
                  <option value="new_launch">New Launch</option>
                </select>
                <select className={dashboardField} data-testid="listing-form-district-select" onChange={(event) => setListingForm({ ...listingForm, district: event.target.value })} value={listingForm.district}>
                  {options.districts.map((district) => <option key={district} value={district}>{district}</option>)}
                </select>
                <select className={dashboardField} data-testid="listing-form-tenure-select" onChange={(event) => setListingForm({ ...listingForm, tenure: event.target.value })} value={listingForm.tenure}>
                  {options.tenures.map((tenure) => <option key={tenure} value={tenure}>{tenure}</option>)}
                </select>
                <select className={dashboardField} data-testid="listing-form-property-type-select" onChange={(event) => setListingForm({ ...listingForm, property_type: event.target.value })} value={listingForm.property_type}>
                  {options.property_types.map((type) => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <input className={dashboardField} data-testid="listing-form-price-input" onChange={(event) => setListingForm({ ...listingForm, price: event.target.value })} type="number" value={listingForm.price} />
                <input className={dashboardField} data-testid="listing-form-sqft-input" onChange={(event) => setListingForm({ ...listingForm, sqft: event.target.value })} type="number" value={listingForm.sqft} />
                <input className={dashboardField} data-testid="listing-form-bedrooms-input" onChange={(event) => setListingForm({ ...listingForm, bedrooms: event.target.value })} type="number" value={listingForm.bedrooms} />
                <input className={dashboardField} data-testid="listing-form-bathrooms-input" onChange={(event) => setListingForm({ ...listingForm, bathrooms: event.target.value })} type="number" value={listingForm.bathrooms} />
                <input className={dashboardField} data-testid="listing-form-status-input" onChange={(event) => setListingForm({ ...listingForm, status: event.target.value })} placeholder="Available / Sold Out / Showflat Open" value={listingForm.status} />
                <input className={dashboardField} data-testid="listing-form-completion-year-input" onChange={(event) => setListingForm({ ...listingForm, completion_year: event.target.value })} type="number" value={listingForm.completion_year} />
              </div>

              <input className={dashboardField} data-testid="listing-form-address-input" onChange={(event) => setListingForm({ ...listingForm, address: event.target.value })} placeholder="Address" value={listingForm.address} />
              <textarea className={`${dashboardField} min-h-32`} data-testid="listing-form-description-input" onChange={(event) => setListingForm({ ...listingForm, description: event.target.value })} placeholder="Description" value={listingForm.description} />
              <input className={dashboardField} data-testid="listing-form-image-url-input" onChange={(event) => setListingForm({ ...listingForm, image_url: event.target.value })} placeholder="Primary image URL (optional when using Admin Media Override)" value={listingForm.image_url} />
              <input className={dashboardField} data-testid="listing-form-brochure-url-input" onChange={(event) => setListingForm({ ...listingForm, brochure_url: event.target.value })} placeholder="Brochure URL (optional)" value={listingForm.brochure_url} />
              <input className={dashboardField} data-testid="listing-form-tour-url-input" onChange={(event) => setListingForm({ ...listingForm, tour_embed_url: event.target.value })} placeholder="Matterport / YouTube / 360 URL" value={listingForm.tour_embed_url} />
              <AdminVirtualStagingPanel
                editingListingId={editingListingId}
                listingForm={listingForm}
                onListingUpdate={setListingForm}
                onPersisted={(listing) => {
                  setListingForm(mapListingToForm(listing));
                  loadDashboard().catch(() => {});
                }}
              />
              <input className={dashboardField} data-testid="listing-form-gallery-input" onChange={(event) => setListingForm({ ...listingForm, gallery_urls: event.target.value })} placeholder="Gallery URLs, comma separated" value={listingForm.gallery_urls} />
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-4" data-testid="listing-form-floorplan-section">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Floor plan</p>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]">Paste public floor plan image URLs, or upload floor plan PDFs. You can use one option or both.</p>
                </div>
                <input className={`${dashboardField} mt-4`} data-testid="listing-form-floorplan-input" onChange={(event) => setListingForm({ ...listingForm, floorplan_urls: event.target.value })} placeholder="Paste floor plan image URLs, comma separated" value={listingForm.floorplan_urls} />
                <div className="my-4 flex items-center gap-3" data-testid="listing-form-floorplan-or-divider">
                  <div className="h-px flex-1 bg-[#001935]/10" />
                  <span className="rounded-full border border-[#D4AF37]/30 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">OR</span>
                  <div className="h-px flex-1 bg-[#001935]/10" />
                </div>
                <div className="rounded-[22px] border border-dashed border-[#D4AF37]/40 bg-white p-4" data-testid="listing-form-floorplan-pdf-upload-box">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#001935]">Upload floor plan PDF</p>
                      <p className="mt-1 text-xs leading-6 text-[#5f5a4e]">PDF only. Multiple PDFs supported. The listing page will show them as viewable plan documents.</p>
                    </div>
                    <label className="outline-button cursor-pointer !px-4 !py-2 text-xs" data-testid="listing-form-floorplan-pdf-upload-trigger">
                      <UploadCloud className="mr-2 h-4 w-4" /> Upload PDF
                      <input accept="application/pdf" className="hidden" data-testid="listing-form-floorplan-pdf-input" multiple onChange={handleFloorplanPdfSelection} type="file" />
                    </label>
                  </div>
                  {uploadingFloorplanPdf ? (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-sm text-[#8b6d16]" data-testid="listing-form-floorplan-pdf-uploading-indicator">
                      <LoaderCircle className="h-4 w-4 animate-spin" /> Uploading floor plan PDF...
                    </div>
                  ) : null}
                  {(listingForm.floorplan_pdf_urls || []).length ? (
                    <div className="mt-4 grid gap-3">
                      {listingForm.floorplan_pdf_urls.map((url, index) => (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-3" data-testid={`listing-form-floorplan-pdf-row-${index + 1}`} key={url}>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#001935]">{getAssetDisplayLabel(url)}</p>
                            <a className="mt-1 inline-flex items-center gap-2 text-xs text-[#8b6d16] underline underline-offset-4" data-testid={`listing-form-floorplan-pdf-open-link-${index + 1}`} href={url} rel="noreferrer" target="_blank">
                              Open uploaded PDF
                            </a>
                          </div>
                          <button className="rounded-full border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-red-600 transition hover:bg-red-100" data-testid={`listing-form-floorplan-pdf-remove-button-${index + 1}`} onClick={() => removePlanPdf(url, "floorplan")} type="button">
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-xs leading-6 text-[#6b6252]" data-testid="listing-form-floorplan-pdf-empty-state">No uploaded floor plan PDFs yet.</p>
                  )}
                </div>
              </div>
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-4" data-testid="listing-form-siteplan-section">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Site plan</p>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]">Paste public site plan image URLs, or upload site plan PDFs. You can use one option or both.</p>
                </div>
                <input className={`${dashboardField} mt-4`} data-testid="listing-form-siteplan-input" onChange={(event) => setListingForm({ ...listingForm, siteplan_urls: event.target.value })} placeholder="Paste site plan image URLs, comma separated" value={listingForm.siteplan_urls} />
                <div className="my-4 flex items-center gap-3" data-testid="listing-form-siteplan-or-divider">
                  <div className="h-px flex-1 bg-[#001935]/10" />
                  <span className="rounded-full border border-[#D4AF37]/30 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">OR</span>
                  <div className="h-px flex-1 bg-[#001935]/10" />
                </div>
                <div className="rounded-[22px] border border-dashed border-[#D4AF37]/40 bg-white p-4" data-testid="listing-form-siteplan-pdf-upload-box">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#001935]">Upload site plan PDF</p>
                      <p className="mt-1 text-xs leading-6 text-[#5f5a4e]">PDF only. Multiple PDFs supported. The listing page will show them as viewable plan documents.</p>
                    </div>
                    <label className="outline-button cursor-pointer !px-4 !py-2 text-xs" data-testid="listing-form-siteplan-pdf-upload-trigger">
                      <UploadCloud className="mr-2 h-4 w-4" /> Upload PDF
                      <input accept="application/pdf" className="hidden" data-testid="listing-form-siteplan-pdf-input" multiple onChange={handleSiteplanPdfSelection} type="file" />
                    </label>
                  </div>
                  {uploadingSiteplanPdf ? (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-sm text-[#8b6d16]" data-testid="listing-form-siteplan-pdf-uploading-indicator">
                      <LoaderCircle className="h-4 w-4 animate-spin" /> Uploading site plan PDF...
                    </div>
                  ) : null}
                  {(listingForm.siteplan_pdf_urls || []).length ? (
                    <div className="mt-4 grid gap-3">
                      {listingForm.siteplan_pdf_urls.map((url, index) => (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-3" data-testid={`listing-form-siteplan-pdf-row-${index + 1}`} key={url}>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#001935]">{getAssetDisplayLabel(url)}</p>
                            <a className="mt-1 inline-flex items-center gap-2 text-xs text-[#8b6d16] underline underline-offset-4" data-testid={`listing-form-siteplan-pdf-open-link-${index + 1}`} href={url} rel="noreferrer" target="_blank">
                              Open uploaded PDF
                            </a>
                          </div>
                          <button className="rounded-full border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-red-600 transition hover:bg-red-100" data-testid={`listing-form-siteplan-pdf-remove-button-${index + 1}`} onClick={() => removePlanPdf(url, "siteplan")} type="button">
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-xs leading-6 text-[#6b6252]" data-testid="listing-form-siteplan-pdf-empty-state">No uploaded site plan PDFs yet.</p>
                  )}
                </div>
              </div>
              <input className={dashboardField} data-testid="listing-form-tags-input" onChange={(event) => setListingForm({ ...listingForm, tags: event.target.value })} placeholder="Tags, comma separated" value={listingForm.tags} />

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-3 rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#42566f]">
                  <input checked={listingForm.featured} data-testid="listing-form-featured-checkbox" onChange={(event) => setListingForm({ ...listingForm, featured: event.target.checked })} type="checkbox" />
                  Mark as featured listing
                </label>
                <label className="flex items-center gap-3 rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#42566f]">
                  <input checked={listingForm.hidden} data-testid="listing-form-hidden-checkbox" onChange={(event) => setListingForm({ ...listingForm, hidden: event.target.checked })} type="checkbox" />
                  Hide from live frontend
                </label>
              </div>

              <ListingMediaManager
                editingListingId={editingListingId}
                fileInputRef={fileInputRef}
                listingForm={listingForm}
                onDropFiles={uploadListingMedia}
                onFileSelection={handleFileSelection}
                onRemove={removeAdminMedia}
                onReorder={reorderAdminMedia}
                onSetPrimary={setPrimaryAdminMedia}
                uploading={uploading}
              />

              <button className="gold-button w-full" data-testid="listing-form-submit-button" disabled={isReadOnlyAgent || isPendingAgent} type="submit">
                <PlusCircle className="mr-2 h-4 w-4" /> {editingListingId ? "Update Listing" : "Create Listing"}
              </button>
            </form>
            ) : (
              <div className={`${panelClass} grid gap-4`} data-testid="listing-management-create-studio-card">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Sovereign Listing Studio</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Open the full-width Create Listing studio</h2>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]">
                    Launch the full-width Create Listing page for sovereign inventory, pre-flight duplicate detection, Gemini description drafting, and signal controls — without the split-dashboard empty space.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-4 rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] px-5 py-5 text-sm leading-7 text-[#5c4921]" data-testid="listing-management-create-studio-note">
                  <span>Open the studio to deploy a sovereign listing, trigger immediate duplicate detection from Project Name + Unit Number, and update the searchable vault once the listing goes Active.</span>
                  <button className="gold-button !w-auto" data-testid="listing-management-open-create-studio-button" onClick={() => { setSovereignStudioListingId(""); setActiveTab("create-listing"); }} type="button">
                    <PlusCircle className="mr-2 h-4 w-4" /> Open Create Listing Studio
                  </button>
                </div>
              </div>
            )}

            <div className={panelClass} data-testid="listing-management-list">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Search & Actions</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Existing listings</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-4 py-2 text-sm text-[#5c4921]" data-testid="admin-listings-count-pill">
                    {listings.length} visible in dashboard
                  </div>
                  {!isAgentView ? (
                    <>
                      <button
                        className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${listingFilters.under_valued_only ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                        data-testid="admin-under-valued-only-toggle"
                        onClick={() => setListingFilters((current) => ({ ...current, under_valued_only: !current.under_valued_only }))}
                        type="button"
                      >
                        Under-valued Only
                      </button>
                      <button
                        className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${listingFilters.sort_by === "value_gap_desc" ? "border-[#D4AF37] bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                        data-testid="admin-value-gap-sort-button"
                        onClick={() => setListingFilters((current) => ({ ...current, sort_by: current.sort_by === "value_gap_desc" ? "updated_at_desc" : "value_gap_desc" }))}
                        type="button"
                      >
                        Value Gap {listingFilters.sort_by === "value_gap_desc" ? "High → Low" : "Recent First"}
                      </button>
                      <button className="gold-button !px-4 !py-2 !text-xs !tracking-[0.18em]" data-testid="admin-under-valued-export-button" onClick={exportUnderValuedCsv} type="button">
                        Export Weekly Under-valued CSV
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                <label className="relative md:col-span-2">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8c8578]" />
                  <input className={`${dashboardField} pl-11`} data-testid="admin-listings-search-input" onChange={(event) => setListingFilters({ ...listingFilters, search: event.target.value })} placeholder="Search by title, address, or district" value={listingFilters.search} />
                </label>
                <select className={dashboardField} data-testid="admin-listings-district-filter" onChange={(event) => setListingFilters({ ...listingFilters, district: event.target.value })} value={listingFilters.district}>
                  <option value="">All districts</option>
                  {options.districts.map((district) => <option key={district} value={district}>{district}</option>)}
                </select>
                <input className={dashboardField} data-testid="admin-listings-min-price-filter" onChange={(event) => setListingFilters({ ...listingFilters, min_price: event.target.value })} placeholder="Min price" type="number" value={listingFilters.min_price} />
                <input className={dashboardField} data-testid="admin-listings-max-price-filter" onChange={(event) => setListingFilters({ ...listingFilters, max_price: event.target.value })} placeholder="Max price" type="number" value={listingFilters.max_price} />
                <select className={dashboardField} data-testid="admin-listings-status-filter" onChange={(event) => setListingFilters({ ...listingFilters, status: event.target.value })} value={listingFilters.status}>
                  <option value="">All statuses</option>
                  <option value="Awaiting Agent Attestation">Awaiting Agent Attestation</option>
                  <option value="Available">Available</option>
                  <option value="Showflat Open">Showflat Open</option>
                  <option value="Now Selling">Now Selling</option>
                  <option value="Sold Out">Sold Out</option>
                </select>
                {canManageUsers ? (
                  <select className={dashboardField} data-testid="admin-listings-owner-filter" onChange={(event) => setListingFilters({ ...listingFilters, owner_id: event.target.value })} value={listingFilters.owner_id}>
                    <option value="">All owners</option>
                    {ownerOptions.map((owner) => <option key={owner.id} value={owner.id}>{owner.name}</option>)}
                  </select>
                ) : null}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3 rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid="admin-bulk-actions-bar">
                <label className="inline-flex items-center gap-3 text-sm font-medium text-[#42566f]">
                  <input checked={listings.length > 0 && selectedListingIds.length === listings.length} data-testid="admin-select-all-listings-checkbox" onChange={toggleSelectAllListings} type="checkbox" />
                  Select all visible
                </label>
                <button className="outline-button !px-4 !py-2" data-testid="admin-bulk-hide-button" disabled={isReadOnlyAgent || isPendingAgent} onClick={() => runBulkAction("hide")} type="button">
                  <EyeOff className="mr-2 h-4 w-4" /> Hide Selected
                </button>
                <button className="outline-button !px-4 !py-2" data-testid="admin-bulk-show-button" disabled={isReadOnlyAgent || isPendingAgent} onClick={() => runBulkAction("show")} type="button">
                  <Eye className="mr-2 h-4 w-4" /> Show Selected
                </button>
                <select className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-sm text-[#001935]" data-testid="admin-bulk-status-select" onChange={(event) => setBulkStatusValue(event.target.value)} value={bulkStatusValue}>
                  <option value="Sold Out">Sold Out</option>
                  <option value="Available">Available</option>
                  <option value="Showflat Open">Showflat Open</option>
                  <option value="Now Selling">Now Selling</option>
                </select>
                <button className="outline-button !px-4 !py-2" data-testid="admin-bulk-status-button" disabled={isReadOnlyAgent || isPendingAgent} onClick={() => runBulkAction("status")} type="button">
                  <CheckSquare className="mr-2 h-4 w-4" /> Apply Status
                </button>
                <button className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100" data-testid="admin-bulk-delete-button" disabled={isReadOnlyAgent || isPendingAgent} onClick={() => runBulkAction("delete")} type="button">
                  <Trash2 className="mr-2 inline h-4 w-4" /> Delete Selected
                </button>
              </div>

              <div className="mt-6 grid gap-4">
                {listings.map((listing) => {
                  const investmentBadge = getInvestmentBadgePresentation(listing);
                  const investmentForecast = investmentForecastCache[listing.id];
                  const isInvestmentExpanded = expandedInvestmentRows.includes(listing.id);
                  const isInvestmentLoading = loadingInvestmentRows.includes(listing.id);

                  return (
                    <div className="rounded-[26px] border border-[#001935]/10 bg-white p-5 shadow-sm" data-testid={`admin-listing-row-${listing.id}`} key={listing.id}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <input checked={selectedListingIds.includes(listing.id)} data-testid={`admin-listing-select-checkbox-${listing.id}`} onChange={() => toggleListingSelection(listing.id)} type="checkbox" />
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xl font-semibold text-[#001935]">{listing.title}</p>
                              {listing.hidden ? <span className="rounded-full bg-[#001935] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-white">Hidden</span> : null}
                              {listing.external_source === "propnex_portfolio" ? <span className="rounded-full bg-[#F6EBC5] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Portfolio Synced</span> : listing.external_source === "xml_syndication" ? <span className="rounded-full bg-[#e6f4ff] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#0f5b8d]">External Feed</span> : listing.external_source === "sovereign_manual" ? <span className="rounded-full bg-[#fff4db] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Manual Listing</span> : <span className="rounded-full bg-[#eef2f6] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]">Manual Listing</span>}
                              {listing.is_unique_gem ? <span className="rounded-full border border-[#D4AF37]/30 bg-[#fffaf1] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`admin-listing-unique-gem-badge-${listing.id}`}>Unique Gem</span> : null}
                              {listing.signal_strength ? <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`admin-listing-signal-strength-${listing.id}`}>Signal {listing.signal_strength}/5</span> : null}
                              {listing.status === "Awaiting Agent Attestation" ? <span className="rounded-full border border-[#D4AF37]/30 bg-[#fff4db] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`admin-listing-attestation-badge-${listing.id}`}>Awaiting Agent Attestation</span> : null}
                              {(listing.admin_media?.gallery || []).length ? <span className="rounded-full bg-[#fff3d8] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Admin Media Override</span> : null}
                              {investmentBadge ? (
                                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${investmentBadge.className}`} data-testid={`admin-listing-investment-badge-${listing.id}`}>
                                  {investmentBadge.emoji ? <span aria-hidden="true" className="hidden sm:inline">{investmentBadge.emoji}</span> : null}
                                  <span className="hidden sm:inline">{investmentBadge.text}</span>
                                  <span className="sm:hidden">{investmentBadge.compactLabel}</span>
                                </span>
                              ) : null}
                              {listing.owner_name ? <span className="rounded-full border border-[#001935]/10 bg-[#f7f8fa] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#42566f]" data-testid={`admin-listing-owner-name-${listing.id}`}>Owner: {listing.owner_name}</span> : null}
                            </div>
                            <p className="mt-2 text-sm text-[#42566f]">{listing.district} • {formatCategoryLabel(listing.category)} • {listing.property_type}</p>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-[#8b6d16]" data-testid={`admin-listing-price-${listing.id}`}>
                                {formatListingPrice(listing)} • {listing.status}
                              </p>
                              {listing.value_gap_percentage !== undefined && listing.value_gap_percentage !== null ? (
                                <span className="rounded-full border border-[#001935]/10 bg-[#f7f8fa] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#42566f]" data-testid={`admin-listing-value-gap-${listing.id}`}>
                                  Value Gap {listing.value_gap_percentage > 0 ? "+" : ""}{Number(listing.value_gap_percentage).toFixed(2)}%
                                </span>
                              ) : null}
                              {listing.is_manual_price_locked ? (
                                <span className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/35 bg-[#fff3d8] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#8b6d16]" data-testid={`admin-listing-manual-price-locked-badge-${listing.id}`}>
                                  <Lock className="h-3.5 w-3.5" /> Manual Price Locked
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  className="outline-button !px-4 !py-2"
                                  data-testid={`admin-investment-tooltip-button-${listing.id}`}
                                  onClick={() => (!canUseInvestmentTools && isAgentView ? openUpgradeModal("investmentTools") : undefined)}
                                  onMouseEnter={() => (canUseInvestmentTools ? loadInvestmentForecastRow(listing.id) : undefined)}
                                  type="button"
                                >
                                  <Info className="mr-2 h-4 w-4" /> AI Logic
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs rounded-[18px] bg-[#001935] px-4 py-3 text-sm leading-7 text-white" side="top">
                                {isInvestmentLoading ? "Loading strategist logic..." : investmentForecast?.investment_verdict_text || "Hover loaded AI logic will appear here."}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <button className="outline-button !px-4 !py-2" data-testid={`admin-toggle-investment-row-button-${listing.id}`} onClick={() => (canUseInvestmentTools ? toggleInvestmentRow(listing.id) : openUpgradeModal("investmentTools"))} type="button">
                            {isInvestmentExpanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />} AI Analysis
                          </button>
                          <button className="outline-button !px-4 !py-2" data-testid={`admin-stage-marketing-button-${listing.id}`} disabled={marketingPosting} onClick={() => stageMarketingCampaignFromListing(listing)} type="button">
                            <Megaphone className="mr-2 h-4 w-4" /> Marketing Studio
                          </button>
                          <button className="outline-button !px-4 !py-2" data-testid={`admin-edit-listing-button-${listing.id}`} disabled={isPendingAgent} onClick={() => {
                            if (listing.external_source === "sovereign_manual") {
                              setSovereignStudioListingId(listing.id);
                              setEditingListingId("");
                              setActiveTab("create-listing");
                              return;
                            }
                            setListingForm(mapListingToForm(listing));
                            setEditingListingId(listing.id);
                            setActiveTab("listings");
                          }} type="button">
                            {listing.external_source === "sovereign_manual" ? "Edit in Studio" : "Edit"}
                          </button>
                          <button className="outline-button !px-4 !py-2" data-testid={`admin-toggle-hide-listing-button-${listing.id}`} disabled={isReadOnlyAgent || isPendingAgent} onClick={() => patchListingField(listing.id, { hidden: !listing.hidden }, listing.hidden ? "Listing restored to live frontend" : "Listing hidden from live frontend")} type="button">
                            {listing.hidden ? <Eye className="mr-2 h-4 w-4" /> : <EyeOff className="mr-2 h-4 w-4" />} {listing.hidden ? "Show" : "Hide"}
                          </button>
                          <button className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100" data-testid={`admin-delete-listing-button-${listing.id}`} disabled={isReadOnlyAgent || isPendingAgent} onClick={() => removeListing(listing.id)} type="button">
                            <Trash2 className="mr-2 inline h-4 w-4" /> Delete
                          </button>
                        </div>
                      </div>
                      {isInvestmentExpanded ? (
                        <div className="mt-4 rounded-[22px] border border-[#D4AF37]/25 bg-[#fffaf1] p-4" data-testid={`admin-investment-expanded-row-${listing.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Signature Investment Analysis</p>
                            <div className="flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-[0.16em]">
                              <span className="rounded-full border border-[#D4AF37]/30 bg-white px-3 py-1 text-[#8b6d16]" data-testid={`admin-investment-expanded-benchmark-${listing.id}`}>
                                {investmentForecast?.benchmark_source === "district" ? "District Mean" : "Project Mean"}
                              </span>
                              {investmentForecast?.value_gap_percentage !== undefined ? (
                                <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-1 text-[#42566f]" data-testid={`admin-investment-expanded-gap-${listing.id}`}>
                                  Value Gap {investmentForecast.value_gap_percentage > 0 ? "+" : ""}{Number(investmentForecast.value_gap_percentage || 0).toFixed(2)}%
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <p className="mt-3 text-sm leading-8 text-[#42566f]" data-testid={`admin-investment-expanded-text-${listing.id}`}>
                            {isInvestmentLoading ? "Loading strategist logic..." : investmentForecast?.investment_verdict_text || "Unable to load AI logic right now."}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "leads" && canViewLeadCRM ? (
          <div className={panelClass} data-testid="leads-management-table">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-3xl font-semibold text-[#001935]">{isAgentView ? "Your listing leads" : "Captured leads"}</h2>
                <p className="mt-2 text-sm text-[#42566f]">
                  {isAgentView ? "Only leads tied to your owned listings appear here, with intent signals scoped to your portfolio." : "Intent profile, live activity, and urgency scoring in one CRM command center."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="lead-quick-filter-group">
                {canManageUsers ? (
                  <select className={dashboardField} data-testid="admin-leads-owner-filter" onChange={(event) => setLeadOwnerFilter(event.target.value)} value={leadOwnerFilter}>
                    <option value="">All owners</option>
                    {ownerOptions.map((owner) => <option key={owner.id} value={owner.id}>{owner.name}</option>)}
                  </select>
                ) : null}
                <select className={dashboardField} data-testid="lead-source-filter" onChange={(event) => setLeadSourceFilter(event.target.value)} value={leadSourceFilter}>
                  {LEAD_SOURCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <select className={dashboardField} data-testid="lead-status-filter" onChange={(event) => setLeadStatusFilter(event.target.value)} value={leadStatusFilter}>
                  {LEAD_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {canUseSalesCommandCenter || !isAgentView ? [
                  { key: "hot", label: "🔥 HOT" },
                  { key: "foreigner", label: "🇸🇬 FOREIGNER" },
                  { key: "pdf_heavy", label: "📄 PDF-HEAVY" },
                  { key: "prime", label: "📍 D10/D9 FOCUSED" },
                  { key: "d15", label: "🌊 D15" },
                ].map((filter) => (
                  <button
                    className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition ${leadQuickFilter === filter.key ? "border-[#D4AF37] bg-[#fff4db] text-[#8b6d16]" : "border-[#001935]/12 bg-white text-[#42566f]"}`}
                    data-testid={`lead-quick-filter-${filter.key}`}
                    key={filter.key}
                    onClick={() => setLeadQuickFilter((current) => current === filter.key ? "all" : filter.key)}
                    type="button"
                  >
                    {filter.label}
                  </button>
                )) : (
                  <button className="gold-button !px-4 !py-2 !text-xs !tracking-[0.18em]" data-testid="agent-upgrade-sales-command-center-button" onClick={() => openUpgradeModal("salesCommandCenter")} type="button">
                    Upgrade to Alpha
                  </button>
                )}
              </div>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-3" data-testid="lead-inbox-analytics-grid">
              <AdminStatCard label="Total Leads" testId="lead-analytics-total" value={summary.lead_pipeline?.total || filteredLeads.length || 0} />
              <AdminStatCard label="Hot Leads" testId="lead-analytics-hot" value={summary.lead_pipeline?.hot || 0} />
              <AdminStatCard label="Viewing Requests" testId="lead-analytics-viewing" value={summary.lead_pipeline?.viewing || 0} />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-inbox-source-breakdown-card">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Source Tracking</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(summary.lead_pipeline?.sources || []).length ? summary.lead_pipeline.sources.map((item) => (
                    <LeadSignalPill key={item.key} label={`${item.label} · ${item.count}`} testId={`lead-source-breakdown-${item.key}`} tone={leadSourceTone[item.key] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                  )) : <p className="text-sm text-[#6b6252]">No source data captured yet.</p>}
                </div>
              </div>
              <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-inbox-status-breakdown-card">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Pipeline Status</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(summary.lead_pipeline?.statuses || []).length ? summary.lead_pipeline.statuses.map((item) => (
                    <LeadSignalPill key={item.key} label={`${item.label} · ${item.count}`} testId={`lead-status-breakdown-${item.key}`} tone={leadStatusTone[item.key] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                  )) : <p className="text-sm text-[#6b6252]">No status updates yet.</p>}
                </div>
              </div>
            </div>
            <div className="mt-6 grid gap-4">
              {filteredLeads.length ? filteredLeads.map((lead) => (
                <div className="cursor-pointer rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 text-left transition hover:border-[#D4AF37]/35 hover:shadow-[0_18px_45px_rgba(0,25,53,0.08)]" data-testid={`lead-row-${lead.id}`} key={lead.id} onClick={() => (canUseSalesCommandCenter || !isAgentView ? setSelectedLead(lead) : undefined)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && (canUseSalesCommandCenter || !isAgentView ? setSelectedLead(lead) : undefined)}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xl font-semibold text-[#001935]">{lead.name}</p>
                        <LeadSignalPill label={lead.lead_source_label || "Direct Contact"} testId={`lead-source-pill-${lead.id}`} tone={leadSourceTone[lead.lead_source || lead.source] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                        <LeadSignalPill label={lead.lead_status_label || "New"} testId={`lead-status-pill-${lead.id}`} tone={leadStatusTone[lead.lead_status || "new"] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                        <LeadSignalPill label={`Agent Email · ${lead.delivery_audit_summary?.agent_status || "pending"}`} testId={`lead-agent-delivery-pill-${lead.id}`} tone={lead.delivery_audit_summary?.agent_status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"} />
                        <LeadSignalPill label={`Archive · ${lead.delivery_audit_summary?.archive_status || "pending"}`} testId={`lead-archive-delivery-pill-${lead.id}`} tone={lead.delivery_audit_summary?.archive_status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : lead.delivery_audit_summary?.archive_status === "failed" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"} />
                        {lead.is_hot_lead ? (
                          <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-red-600" data-testid={`lead-hot-badge-${lead.id}`}>
                            🔥 HOT LEAD
                          </span>
                        ) : null}
                        {lead.is_recently_active ? (
                          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700" data-testid={`lead-active-pulse-${lead.id}`}>
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                            </span>
                            Live now
                          </span>
                        ) : null}
                      </div>
                      {isTeamLeaderView && lead.is_hot_lead ? (
                        <button className="mt-3 rounded-full border border-[#D4AF37]/35 bg-[#fff4db] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`lead-assign-button-${lead.id}`} onClick={(event) => { event.stopPropagation(); setSelectedLead(lead); }} type="button">
                          Assign Lead
                        </button>
                      ) : null}
                      <p className="mt-1 text-sm text-[#42566f]">{lead.email} • {lead.phone}</p>
                      <p className="mt-2 text-sm text-[#42566f]">{lead.listing_title || lead.inquiry_type || "General Enquiry"}</p>
                      <p className="mt-2 text-sm text-[#6b6252]" data-testid={`lead-source-detail-${lead.id}`}>
                        {lead.source_history?.[0]?.detail || lead.captured_context || "Lead source detail pending"}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2" data-testid={`lead-quick-actions-${lead.id}`}>
                        <a className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1A1A1A]" data-testid={`lead-whatsapp-action-${lead.id}`} href={buildLeadWhatsAppHref(lead)} rel="noreferrer" target="_blank">
                          <MessageCircle className="h-3.5 w-3.5" /> Quick-Action WhatsApp
                        </a>
                        <a className="inline-flex items-center justify-center gap-2 rounded-full border border-[#001935]/12 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#001935]" data-testid={`lead-email-action-${lead.id}`} href={buildLeadEmailHref(lead)}>
                          <Mail className="h-3.5 w-3.5" /> Email Trigger
                        </a>
                      </div>
                      {canUseSalesCommandCenter || !isAgentView ? (
                        <div className="mt-4 rounded-[20px] border border-[#D4AF37]/25 bg-white px-4 py-4" data-testid={`lead-intent-profile-${lead.id}`}>
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Intent Profile</p>
                          <p className="mt-2 text-sm leading-7 text-[#42566f]">{lead.intent_profile || "No modeled intent signals yet"}</p>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-[20px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`lead-basic-crm-panel-${lead.id}`}>
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Basic CRM</p>
                          <p className="mt-2 text-sm leading-7 text-[#42566f]">Captured contact details only. Upgrade to Alpha Investor for activity timelines, intent scoring, and HOT lead prioritisation.</p>
                        </div>
                      )}
                    </div>
                    {(canUseSalesCommandCenter || !isAgentView) ? <div className="w-full max-w-[260px] rounded-[20px] border border-[#001935]/10 bg-white px-4 py-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Lead Intensity</p>
                        <p className="text-lg font-semibold text-[#001935]" data-testid={`lead-intensity-score-${lead.id}`}>{lead.lead_intensity_score || 0}</p>
                      </div>
                      <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#edf1f5]" data-testid={`lead-intensity-meter-${lead.id}`}>
                        <div className="h-full rounded-full bg-[linear-gradient(90deg,#D4AF37_0%,#f4bf4f_45%,#ef4444_100%)]" style={{ width: `${lead.lead_intensity_score || 0}%` }} />
                      </div>
                      <div className="mt-4 text-sm text-[#6b6252]">
                        <p className="uppercase tracking-[0.18em] text-[#8b6d16]">Last Active</p>
                        <p className="mt-1" data-testid={`lead-last-active-${lead.id}`}>{lead.last_active_at ? formatDate(lead.last_active_at) : "No activity yet"}</p>
                      </div>
                      {(lead.viewing_requests || []).length ? <div className="mt-4 rounded-[16px] border border-emerald-200 bg-emerald-50 px-3 py-3" data-testid={`lead-viewing-summary-${lead.id}`}>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">Private Viewing</p>
                        <p className="mt-1 text-sm text-emerald-900">{(lead.viewing_requests || []).slice(-1)[0]?.preferred_date} • {(lead.viewing_requests || []).slice(-1)[0]?.specific_time}</p>
                      </div> : null}
                    </div> : null}
                  </div>
                  {(canUseSalesCommandCenter || !isAgentView) ? <div className="mt-4 rounded-[20px] border border-[#001935]/10 bg-white px-4 py-4" data-testid={`lead-activity-timeline-${lead.id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Activity Timeline</p>
                    <div className="mt-3 grid gap-3">
                      {(lead.activity_timeline || []).length ? (
                        lead.activity_timeline.map((activity, index) => (
                          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#001935]/6 pb-3 last:border-b-0 last:pb-0" data-testid={`lead-activity-item-${lead.id}-${index}`} key={`${activity.created_at}-${index}`}>
                            <p className="text-sm leading-7 text-[#42566f]">{leadActivityIcon[activity.event_type] || "•"} {activity.label}</p>
                            <p className="text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{formatDate(activity.created_at)}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-[#6b6252]">No intent activity captured yet.</p>
                      )}
                    </div>
                  </div> : null}
                </div>
              )) : <div className="rounded-[24px] border border-[#001935]/10 bg-white px-5 py-6 text-sm text-[#42566f]" data-testid="lead-inbox-empty-state">No leads match the current source and status filters yet.</div>}
            </div>

            <Sheet open={Boolean(selectedLead) && (canUseSalesCommandCenter || !isAgentView)} onOpenChange={(open) => { if (!open) setSelectedLead(null); }}>
              <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-detail-drawer" side="right">
                {selectedLead ? (
                  <>
                    <SheetHeader>
                      <SheetTitle className="text-left text-2xl font-semibold text-[#001935]">{selectedLead.name}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 grid gap-4">
                      <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5">
                        <div className="flex flex-wrap items-center gap-2">
                          <LeadSignalPill label={selectedLead.lead_source_label || "Direct Contact"} testId="lead-detail-source-pill" tone={leadSourceTone[selectedLead.lead_source || selectedLead.source] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                          <LeadSignalPill label={selectedLead.lead_status_label || "New"} testId="lead-detail-status-pill" tone={leadStatusTone[selectedLead.lead_status || "new"] || "border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]"} />
                          <LeadSignalPill label={`Agent Email · ${selectedLead.delivery_audit_summary?.agent_status || "pending"}`} testId="lead-detail-agent-delivery-pill" tone={selectedLead.delivery_audit_summary?.agent_status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"} />
                          <LeadSignalPill label={`Archive · ${selectedLead.delivery_audit_summary?.archive_status || "pending"}`} testId="lead-detail-archive-delivery-pill" tone={selectedLead.delivery_audit_summary?.archive_status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : selectedLead.delivery_audit_summary?.archive_status === "failed" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"} />
                          {selectedLead.is_hot_lead ? <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-red-600">🔥 HOT LEAD</span> : null}
                          {selectedLead.is_recently_active ? (
                            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">
                              <span className="relative flex h-2.5 w-2.5">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                              </span>
                              High Speed-to-Lead
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-3 text-sm text-[#42566f]">{selectedLead.email} • {selectedLead.phone}</p>
                        <p className="mt-2 text-sm text-[#42566f]" data-testid="lead-detail-source-context">{selectedLead.captured_context || selectedLead.source_history?.[0]?.detail || "Source context pending"}</p>
                        <p className="mt-2 text-sm text-[#42566f]">Last Active: {selectedLead.last_active_at ? formatDate(selectedLead.last_active_at) : "No recent activity"}</p>
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="lead-detail-quick-actions">
                          <a className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1A1A1A]" data-testid="lead-detail-whatsapp-action" href={buildLeadWhatsAppHref(selectedLead)} rel="noreferrer" target="_blank">
                            <MessageCircle className="h-3.5 w-3.5" /> Quick-Action WhatsApp
                          </a>
                          <a className="inline-flex items-center justify-center gap-2 rounded-full border border-[#001935]/12 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#001935]" data-testid="lead-detail-email-action" href={buildLeadEmailHref(selectedLead)}>
                            <Mail className="h-3.5 w-3.5" /> Email Trigger
                          </a>
                        </div>
                        <div className="mt-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Lead Intensity</p>
                            <p className="text-lg font-semibold text-[#001935]">{selectedLead.lead_intensity_score || 0}/100</p>
                          </div>
                          <div className="mt-3 h-3 overflow-hidden rounded-full bg-[#edf1f5]">
                            <div className="h-full rounded-full bg-[linear-gradient(90deg,#D4AF37_0%,#f4bf4f_45%,#ef4444_100%)]" style={{ width: `${selectedLead.lead_intensity_score || 0}%` }} />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="lead-detail-ai-sales-insight">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">AI Sales Insights</p>
                        <p className="mt-3 text-sm leading-8 text-[#42566f]">{selectedLead.ai_sales_insight || "No AI sales insight available yet."}</p>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-status-panel">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Lead Inbox Status</p>
                        <div className="mt-4 grid gap-3">
                          <select className={dashboardField} data-testid="lead-status-select" onChange={(event) => setLeadStatusDraft((current) => ({ ...current, lead_status: event.target.value }))} value={leadStatusDraft.lead_status}>
                            {LEAD_STATUS_OPTIONS.filter((option) => option.value !== "all").map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                          <textarea className={`${dashboardField} min-h-[110px]`} data-testid="lead-status-note-input" onChange={(event) => setLeadStatusDraft((current) => ({ ...current, note: event.target.value }))} placeholder="Follow-up note or next action" value={leadStatusDraft.note} />
                          <button className="gold-button !px-4 !py-2" data-testid="lead-status-save-button" disabled={savingLeadStatus} onClick={saveLeadStatus} type="button">
                            {savingLeadStatus ? "Saving status..." : "Save Lead Status"}
                          </button>
                        </div>
                      </div>

                      {isTeamLeaderView ? (
                        <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-assignment-panel">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Lead Assignment</p>
                            {selectedLead.assigned_agent_name ? <p className="text-sm text-[#42566f]">Assigned: {selectedLead.assigned_agent_name}</p> : null}
                          </div>
                          <div className="mt-4 flex flex-wrap gap-3">
                            <select className={dashboardField} data-testid="lead-detail-assign-agent-select" defaultValue={selectedLead.assigned_agent_id || ""} onChange={(event) => setSelectedLead({ ...selectedLead, assigned_agent_id: event.target.value })}>
                              <option value="">Select junior agent</option>
                              {teamMembers.filter((member) => member.team_member_status === "Active").map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                            </select>
                            <button className="gold-button !px-4 !py-2" data-testid="lead-detail-assign-button" disabled={!selectedLead.assigned_agent_id} onClick={() => assignLead(selectedLead.id, selectedLead.assigned_agent_id)} type="button">
                              Assign Lead
                            </button>
                          </div>
                        </div>
                      ) : null}

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Intent Profile</p>
                        <p className="mt-3 text-sm leading-8 text-[#42566f]">{selectedLead.intent_profile || "No intent profile yet"}</p>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-activity-timeline">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Activity Timeline</p>
                        <div className="mt-4 grid gap-3">
                          {(selectedLead.activity_timeline || []).length ? (selectedLead.activity_timeline || []).map((activity, index) => (
                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#001935]/6 pb-3 last:border-b-0 last:pb-0" key={`${activity.created_at}-${index}`}>
                              <p className="text-sm leading-7 text-[#42566f]">{leadActivityIcon[activity.event_type] || "•"} {activity.label}</p>
                              <p className="text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{formatDate(activity.created_at)}</p>
                            </div>
                          )) : <p className="text-sm text-[#6b6252]">No tracked activity yet.</p>}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-delivery-audit-log">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Delivery Audit Log</p>
                        <div className="mt-4 grid gap-3">
                          {(selectedLead.delivery_audit_log || []).length ? selectedLead.delivery_audit_log.map((entry) => (
                            <div className="rounded-[18px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid={`lead-detail-delivery-entry-${entry.id}`} key={entry.id}>
                              <div className="flex flex-wrap items-center gap-2">
                                <LeadSignalPill label={entry.channel === "master_archive" ? "Master Archive" : "Agent Email"} testId={`lead-delivery-channel-${entry.id}`} tone="border-[#001935]/10 bg-white text-[#42566f]" />
                                <LeadSignalPill label={entry.status || "pending"} testId={`lead-delivery-status-${entry.id}`} tone={entry.status === "sent" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : entry.status === "failed" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"} />
                              </div>
                              <p className="mt-3 text-sm font-semibold text-[#001935]">{entry.recipient}</p>
                              <p className="mt-1 text-sm text-[#42566f]">{entry.subject || "Lead delivery event"}</p>
                              <p className="mt-2 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{entry.created_at ? formatDate(entry.created_at) : "Timestamp pending"}</p>
                              {entry.error_message ? <p className="mt-2 text-sm text-red-600">{entry.error_message}</p> : null}
                            </div>
                          )) : <p className="text-sm text-[#6b6252]">No delivery events logged yet.</p>}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-source-history-panel">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Source Journey</p>
                        <div className="mt-4 grid gap-3">
                          {(selectedLead.source_history || []).length ? selectedLead.source_history.map((item, index) => (
                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#001935]/6 pb-3 last:border-b-0 last:pb-0" data-testid={`lead-detail-source-history-${index}`} key={item.id || `${item.created_at}-${index}`}>
                              <div>
                                <p className="text-sm font-semibold text-[#001935]">{item.label}</p>
                                <p className="mt-1 text-sm text-[#42566f]">{item.detail || "No detail captured"}</p>
                              </div>
                              <p className="text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{formatDate(item.created_at)}</p>
                            </div>
                          )) : <p className="text-sm text-[#6b6252]">No source history captured yet.</p>}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-viewings-panel">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Private Viewing Booking Flow</p>
                        <div className="mt-4 grid gap-3">
                          {(selectedLead.viewing_requests || []).length ? selectedLead.viewing_requests.map((request, index) => (
                            <div className="rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-4" data-testid={`lead-detail-viewing-request-${index}`} key={request.id || `${request.created_at}-${index}`}>
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="text-base font-semibold text-emerald-900">{request.project_name}</p>
                                <LeadSignalPill label={request.status || "requested"} testId={`lead-detail-viewing-status-${index}`} tone="border-emerald-200 bg-white text-emerald-700" />
                              </div>
                              <p className="mt-2 text-sm text-emerald-900">{request.preferred_date} • {request.time_window} • {request.specific_time}</p>
                              <p className="mt-2 text-xs uppercase tracking-[0.16em] text-emerald-700">Requested {formatDate(request.created_at)}</p>
                            </div>
                          )) : <p className="text-sm text-[#6b6252]">No structured viewing requests for this lead yet.</p>}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#001935]/10 bg-white p-5" data-testid="lead-detail-compliance-panel">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">2026 Compliance Checklist</p>
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          {[
                            ["cdd_completed", "Customer Due Diligence completed"],
                            ["source_of_funds_verified", "Source of funds verified"],
                            ["sanctions_screened", "Sanctions screening completed"],
                            ["beneficial_owner_checked", "Beneficial owner checked"],
                          ].map(([key, label]) => (
                            <label className="flex items-center gap-3 text-sm text-[#42566f]" key={key}>
                              <input checked={Boolean(leadComplianceDraft[key])} data-testid={`lead-compliance-checkbox-${key}`} onChange={(event) => setLeadComplianceDraft({ ...leadComplianceDraft, [key]: event.target.checked })} type="checkbox" />
                              <span>{label}</span>
                            </label>
                          ))}
                        </div>
                        <div className="mt-4 grid gap-3">
                          <input className={dashboardField} data-testid="lead-compliance-deal-stage-input" onChange={(event) => setLeadComplianceDraft({ ...leadComplianceDraft, deal_stage: event.target.value })} placeholder="Deal stage" value={leadComplianceDraft.deal_stage} />
                          <textarea className={`${dashboardField} min-h-[110px]`} data-testid="lead-compliance-risk-notes-input" onChange={(event) => setLeadComplianceDraft({ ...leadComplianceDraft, risk_notes: event.target.value })} placeholder="Risk notes / due diligence observations" value={leadComplianceDraft.risk_notes} />
                        </div>
                        <div className="mt-4 flex items-center justify-between gap-3">
                          <p className="text-sm text-[#42566f]">Status: <span className="font-semibold text-[#001935]">{selectedLead.compliance_status || "pending"}</span></p>
                          <button className="gold-button !px-4 !py-2" data-testid="lead-compliance-save-button" onClick={saveLeadCompliance} type="button">
                            Save Checklist
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </SheetContent>
            </Sheet>
          </div>
        ) : null}

        <Sheet open={showMagicDropReviewSheet && Boolean(magicDropReviewData)} onOpenChange={setShowMagicDropReviewSheet}>
          <SheetContent className="w-full overflow-y-auto border-l border-[#001935]/10 bg-[#fdfbf7] sm:max-w-[96vw]" data-testid="magic-drop-review-drawer" side="right">
            {magicDropReviewData ? (
              <>
                <SheetHeader>
                  <SheetTitle className="text-left text-2xl font-semibold text-[#001935]" data-testid="magic-drop-review-title">
                    Manual Import review for {magicDropReviewData.source_filename || "your latest upload"}
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                  <div className="grid gap-4 xl:sticky xl:top-0 xl:self-start">
                    <div className="rounded-[28px] border border-[#D4AF37]/25 bg-[#fffaf1] p-5" data-testid="magic-drop-review-summary-card">
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Review summary</p>
                      <p className="mt-3 text-sm leading-7 text-[#42566f]">
                        Compare the extracted draft listings against the raw source text, deselect anything that needs more work, then publish only the approved drafts.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="magic-drop-review-detected-count">
                          {magicDropReviewData.review_listings.length} extracted
                        </span>
                        <span className="rounded-full border border-[#D4AF37]/25 bg-[#fff7e7] px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid="magic-drop-review-approved-count">
                          {magicDropApprovedListingIds.length} approved
                        </span>
                      </div>
                    </div>

                    <div className="rounded-[28px] border border-[#001935]/10 bg-white p-5" data-testid="magic-drop-review-raw-text-panel">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Raw source text</p>
                        <span className="rounded-full border border-[#001935]/10 bg-[#f7f8fa] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="magic-drop-review-source-file-pill">
                          {magicDropReviewData.source_filename || "Uploaded export"}
                        </span>
                      </div>
                      <pre className="mt-4 max-h-[58vh] overflow-auto whitespace-pre-wrap rounded-[22px] bg-[#fcfaf5] px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="magic-drop-review-raw-text-content">
                        {magicDropReviewData.raw_text || "No raw text preview available for this file."}
                      </pre>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[28px] border border-[#001935]/10 bg-white p-5" data-testid="magic-drop-review-actions-bar">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Extracted draft listings</p>
                        <p className="mt-2 text-sm text-[#42566f]">Each card shows the AI-structured draft beside a source snippet from the uploaded file.</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button className="outline-button" data-testid="magic-drop-review-select-all-button" onClick={() => setMagicDropApprovedListingIds((magicDropReviewData.review_listings || []).map((item) => item.listing_id).filter(Boolean))} type="button">
                          Select all
                        </button>
                        <button className="outline-button" data-testid="magic-drop-review-clear-selection-button" onClick={() => setMagicDropApprovedListingIds([])} type="button">
                          Clear selection
                        </button>
                        <button className="gold-button" data-testid="magic-drop-review-publish-approved-button" disabled={!magicDropApprovedListingIds.length || magicDropPublishing} onClick={publishApprovedMagicDropListings} type="button">
                          {magicDropPublishing ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <CheckSquare className="mr-2 h-4 w-4" />}
                          {magicDropPublishing ? "Publishing approved drafts..." : "Publish approved drafts"}
                        </button>
                      </div>
                    </div>

                    {(magicDropReviewData.review_listings || []).map((listing, index) => {
                      const isApproved = magicDropApprovedListingIds.includes(listing.listing_id);
                      const districtSignal = getMagicDropDistrictSignalPresentation(listing);
                      return (
                        <div className="rounded-[28px] border border-[#001935]/10 bg-white p-5 shadow-sm" data-testid={`magic-drop-review-card-${listing.listing_id || index}`} key={listing.listing_id || `${listing.title}-${index}`}>
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <input checked={isApproved} data-testid={`magic-drop-review-approve-checkbox-${listing.listing_id || index}`} onChange={() => toggleMagicDropApprovedListing(listing.listing_id)} type="checkbox" />
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-xl font-semibold text-[#001935]" data-testid={`magic-drop-review-title-${listing.listing_id || index}`}>{listing.title || "Untitled draft"}</p>
                                  <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${listing.was_existing ? "border border-[#001935]/10 bg-[#f7f8fa] text-[#42566f]" : "border border-emerald-200 bg-emerald-50 text-emerald-700"}`} data-testid={`magic-drop-review-sync-mode-${listing.listing_id || index}`}>
                                    {listing.was_existing ? "Updated existing draft" : "New draft"}
                                  </span>
                                  <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${listing.status === "Available" ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "border border-[#D4AF37]/25 bg-[#fff4db] text-[#8b6d16]"}`} data-testid={`magic-drop-review-status-${listing.listing_id || index}`}>
                                    {listing.status || "Draft"}
                                  </span>
                                </div>
                                <p className="mt-2 text-sm text-[#42566f]" data-testid={`magic-drop-review-source-snippet-${listing.listing_id || index}`}>
                                  Source match: {buildMagicDropSourceSnippet(magicDropReviewData.raw_text, listing)}
                                </p>
                              </div>
                            </div>
                            <button className="outline-button !px-4 !py-2" data-testid={`magic-drop-review-open-listing-button-${listing.listing_id || index}`} onClick={() => openMagicDropListingInManager(listing.listing_id)} type="button">
                              Open in Listings
                            </button>
                          </div>

                          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-property-type-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Property type</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{listing.property_type || "Not detected"}</p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-district-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">District</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{listing.district || "Not detected"}</p>
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${districtSignal.badgeClassName}`} data-testid={`magic-drop-review-district-source-badge-${listing.listing_id || index}`}>
                                  {districtSignal.label}
                                </span>
                              </div>
                              <p className="mt-3 text-xs leading-6 text-[#5f5a4e]" data-testid={`magic-drop-review-district-source-helper-${listing.listing_id || index}`}>
                                {districtSignal.helperText}
                              </p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-price-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Price</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{formatMagicDropReviewPrice(listing.price)}</p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-bedrooms-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Bedrooms</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{listing.bedrooms || 0}</p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-bathrooms-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Bathrooms</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{listing.bathrooms || 0}</p>
                            </div>
                          </div>

                          <div className="mt-3 grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-size-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Size</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{Number(listing.size_sqft || 0) ? `${Number(listing.size_sqft).toLocaleString()} sqft` : "Not detected"}</p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-address-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Address</p>
                              <p className="mt-2 text-sm leading-7 text-[#42566f]">{listing.address || (listing.postal_code ? `Singapore ${listing.postal_code}` : "Not detected")}</p>
                            </div>
                          </div>

                          <div className="mt-3 grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-postal-code-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Postal code</p>
                              <p className="mt-2 text-sm font-semibold text-[#001935]">{listing.postal_code || "Not detected"}</p>
                            </div>
                            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-4" data-testid={`magic-drop-review-field-description-${listing.listing_id || index}`}>
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Description</p>
                              <p className="mt-2 text-sm leading-7 text-[#42566f]">{listing.description || "No description extracted."}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}
          </SheetContent>
        </Sheet>

        {!loading && activeTab === "users" && canManageUsers ? (
          <div className="pb-32" ref={usersTabRef}>
            <AdminUsersCommandCenter
              filteredUsers={filteredUsers}
              formatDate={formatDate}
              onCreateUser={openCreateUserDrawer}
              onDeleteUser={deleteUser}
              onEditUser={openEditUserDrawer}
              onSortChange={handleUserSortChange}
              onSortHeaderClick={handleUserSortHeaderClick}
              planFilter={userPlanFilter}
              planOptions={PLAN_TYPE_OPTIONS}
              searchValue={userSearchTerm}
              setPlanFilter={setUserPlanFilter}
              setSearchValue={setUserSearchTerm}
              sortState={userSort}
            />

            <AdminUserDrawer
              member={null}
              mode="create"
              onDelete={() => {}}
              onFieldChange={updateNewUserField}
              onOpenChange={handleUserDrawerOpenChange}
              onSubmit={createUser}
              open={isUserDrawerOpen && userDrawerMode === "create"}
              planOptions={PLAN_TYPE_OPTIONS}
              roleOptions={USER_ROLE_OPTIONS}
              subscriptionStatusOptions={SUBSCRIPTION_STATUS_OPTIONS}
              value={newUser}
            />

            {editingUser && editingUserDraft ? (
              <AdminUserDrawer
                member={editingUser}
                mode="edit"
                onDelete={deleteUser}
                onFieldChange={updateEditingUserField}
                onOpenChange={handleUserDrawerOpenChange}
                onSubmit={(event) => {
                  event.preventDefault();
                  saveUserDraft(editingUser.id);
                }}
                open={isUserDrawerOpen && userDrawerMode === "edit"}
                planOptions={PLAN_TYPE_OPTIONS}
                roleOptions={USER_ROLE_OPTIONS}
                subscriptionStatusOptions={SUBSCRIPTION_STATUS_OPTIONS}
                value={editingUserDraft}
              />
            ) : null}
          </div>
        ) : null}

        {!loading && activeTab === "partners" && canManageUsers ? (
          <div className="pb-32">
            <AdminReferralPartnerLedger
              creatingPartner={creatingReferralPartner}
              onCreatePartner={createReferralPartner}
              onRunAlexRehearsal={runAlexNgReferralRehearsal}
              onUpdateCommissionStatus={updateReferralCommissionStatus}
              referralSummary={referralSummary}
              rehearsingPartnerId={rehearsingPartnerId}
              updatingCommissionId={updatingReferralCommissionId}
            />
          </div>
        ) : null}

        {!loading && activeTab === "team" && isTeamLeaderView ? (
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]" data-testid="elite-team-dashboard">
            <div className="grid gap-6">
              <form className={`${panelClass} grid gap-4`} data-testid="team-branding-form" onSubmit={saveTeamBranding}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Team Branding</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Shared team identity</h2>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]">Your team logo and team name override individual branding on agent profile paths and Exit Memo PDFs.</p>
                </div>
                <input className={dashboardField} data-testid="team-branding-name-input" onChange={(event) => setTeamBranding({ ...teamBranding, team_name: event.target.value })} placeholder="Team name" value={teamBranding.team_name} />
                <CloudinaryImageField
                  fitGuidance="Upload a padded, centered team mark so it scales cleanly in the header, footer, and Exit Memo PDF branding areas."
                  folder="uploads/users/team-logos"
                  helperText="The shared team logo now uploads directly and previews before saving."
                  label="Team logo"
                  onChange={(nextValue) => setTeamBranding((current) => ({ ...current, team_logo_url: nextValue }))}
                  previewAlt="Team branding logo preview"
                  previewMode="contain"
                  testIdPrefix="team-branding-logo"
                  value={teamBranding.team_logo_url}
                />
                <button className="gold-button w-full" data-testid="team-branding-save-button" type="submit">Save Team Branding</button>
              </form>

              <form className={`${panelClass} grid gap-4`} data-testid="team-invite-form" onSubmit={inviteTeamMember}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Seat Management</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Invite junior agents</h2>
                </div>
                <input className={dashboardField} data-testid="team-invite-name-input" onChange={(event) => setTeamInvite({ ...teamInvite, name: event.target.value })} placeholder="Full name" required value={teamInvite.name} />
                <input className={dashboardField} data-testid="team-invite-email-input" onChange={(event) => setTeamInvite({ ...teamInvite, email: event.target.value })} placeholder="Email" required type="email" value={teamInvite.email} />
                <input className={dashboardField} data-testid="team-invite-cea-input" onChange={(event) => setTeamInvite({ ...teamInvite, cea_registration_number: event.target.value })} placeholder="CEA registration number" required value={teamInvite.cea_registration_number} />
                <input className={dashboardField} data-testid="team-invite-phone-input" onChange={(event) => setTeamInvite({ ...teamInvite, phone: event.target.value })} placeholder="Phone" value={teamInvite.phone} />
                <select className={dashboardField} data-testid="team-invite-status-select" onChange={(event) => setTeamInvite({ ...teamInvite, team_member_status: event.target.value })} value={teamInvite.team_member_status}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <button className="gold-button w-full" data-testid="team-invite-submit-button" type="submit">Invite Agent</button>
              </form>
            </div>

            <div className="grid gap-6">
              <div className={`${panelClass} grid gap-4`} data-testid="team-compliance-health-panel">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">2026 Compliance Health</p>
                  <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Paperwork visibility across your team</h2>
                </div>
                <div className="grid gap-4 md:grid-cols-4">
                  {[
                    ["Total Team Leads", teamComplianceHealth?.total_team_leads || 0],
                    ["Compliant", teamComplianceHealth?.compliant_leads || 0],
                    ["Pending", teamComplianceHealth?.pending_leads || 0],
                    ["Missing", teamComplianceHealth?.missing_paperwork || 0],
                  ].map(([label, value]) => (
                    <div className="rounded-[20px] border border-[#001935]/10 bg-[#fffaf1] p-4" key={label}>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{label}</p>
                      <p className="mt-2 text-3xl font-semibold text-[#001935]">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="grid gap-3">
                  {(teamComplianceHealth?.agent_breakdown || []).map((agent) => (
                    <div className="rounded-[20px] border border-[#001935]/10 bg-white p-4" key={agent.agent_name}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-base font-semibold text-[#001935]">{agent.agent_name}</p>
                        <p className="text-sm text-[#42566f]">Compliant {agent.compliant} • Pending {agent.pending}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={panelClass} data-testid="team-member-list">
                <h2 className="text-3xl font-semibold text-[#001935]">Junior agent seats</h2>
                <div className="mt-6 grid gap-4">
                  {teamMembers.map((member) => (
                    <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`team-member-row-${member.id}`} key={member.id}>
                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="text-lg font-semibold text-[#001935]">{member.name}</p>
                          <p className="mt-2 text-sm text-[#42566f]">{member.email} • {member.cea_registration_number}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          <span className="rounded-full border border-[#001935]/10 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`team-member-status-pill-${member.id}`}>{member.team_member_status}</span>
                          <button className="outline-button !px-4 !py-2" data-testid={`team-member-toggle-status-button-${member.id}`} onClick={() => updateTeamMemberStatus(member.id, member.team_member_status === "Active" ? "Inactive" : "Active")} type="button">
                            Set {member.team_member_status === "Active" ? "Inactive" : "Active"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {showAgentImportModal ? (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[#001935]/55 px-4 py-8 backdrop-blur-sm" data-testid="agent-propnex-import-modal-overlay">
            <div className="w-full max-w-2xl rounded-[30px] border border-[#D4AF37]/20 bg-white p-6 shadow-[0_30px_90px_rgba(0,25,53,0.28)] md:p-8" data-testid="agent-propnex-import-modal">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Compliant Portfolio Import</p>
                  <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Start the authorised sync for your active Signature profile</h3>
                  <p className="mt-3 text-sm leading-7 text-[#42566f]">This launches the live portfolio sync for the signed-in Alpha Investor session and attaches the refreshed inventory directly to your current agent workspace.</p>
                </div>
                <button className="outline-button !px-4 !py-2" data-testid="agent-propnex-import-close-button" onClick={() => setShowAgentImportModal(false)} type="button">
                  Close
                </button>
              </div>
              <form className="mt-6 grid gap-4" data-testid="agent-propnex-import-form" onSubmit={submitAgentImport}>
                <div className="grid gap-3 rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4 text-sm text-[#42566f]" data-testid="agent-propnex-import-session-summary">
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Active profile path</span>
                    <p className="mt-2 font-semibold text-[#001935]" data-testid="agent-propnex-import-path-value">{publicSiteDisplay}</p>
                  </div>
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Signed-in identity</span>
                    <p className="mt-2 font-semibold text-[#001935]" data-testid="agent-propnex-import-email-value">{user.email}</p>
                  </div>
                </div>
                <label className="flex items-start gap-3 rounded-[24px] border border-[#D4AF37]/20 bg-[#fffaf1] px-4 py-4 text-sm leading-7 text-[#42566f]" data-testid="agent-propnex-consent-checkbox-row">
                  <input checked={syncConsentChecked} data-testid="agent-propnex-consent-checkbox" onChange={(event) => setSyncConsentChecked(event.target.checked)} required type="checkbox" />
                  <span>I authorize SGPropPortal to sync my authorised external portfolio into the active agent workspace for this signed-in profile.</span>
                </label>
                <div className="rounded-[24px] border border-[#001935]/10 bg-white px-4 py-4 text-sm leading-7 text-[#6b6252]" data-testid="agent-propnex-legal-copy">
                  This action is tied to your current logged-in session. Refreshed synced listings will be assigned to your active identity and surfaced across the Signature theme as soon as the sync completes.
                </div>
                <button className="gold-button w-full" data-testid="agent-propnex-import-submit-button" disabled={syncingPortfolio || !syncConsentChecked || isReadOnlyAgent} type="submit">
                  {syncingPortfolio ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />} Portfolio Sync
                </button>
              </form>
            </div>
          </div>
        ) : null}

        {lockedFeatureKey ? (
          <div className="fixed inset-0 z-[85] flex items-center justify-center bg-[#001935]/55 px-4 py-8 backdrop-blur-sm" data-testid="plan-upgrade-modal-overlay">
            <div className="w-full max-w-xl rounded-[30px] border border-[#D4AF37]/20 bg-white p-6 shadow-[0_30px_90px_rgba(0,25,53,0.28)] md:p-8" data-testid="plan-upgrade-modal">
              {(() => {
                const billingPackage = getBillingPackage(getPackageIdForFeature(lockedFeatureKey));
                return (
                  <>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Upgrade Trigger</p>
              <h3 className="mt-2 text-3xl font-semibold text-[#001935]">{getUpgradeCopy(lockedFeatureKey).title}</h3>
              <p className="mt-4 text-sm leading-8 text-[#42566f]">{getUpgradeCopy(lockedFeatureKey).body}</p>
              <div className="mt-6 rounded-[24px] border border-[#D4AF37]/20 bg-[#fffaf1] px-4 py-4 text-sm leading-7 text-[#6b6252]" data-testid="plan-upgrade-modal-benefits">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid="plan-upgrade-modal-package-price">{billingPackage.amountLabel}</p>
                <p className="mt-2" data-testid="plan-upgrade-modal-package-description">{billingPackage.description}</p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="gold-button" data-testid="plan-upgrade-modal-primary-button" disabled={Boolean(billingBusyKey)} onClick={() => launchPlanAction(billingPackage.packageId, { portalMessage: "Opening Stripe billing controls for this plan change." })} type="button">
                  {billingBusyKey === billingPackage.packageId ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {billingPackage.actionLabel}
                </button>
                <button className="outline-button" data-testid="plan-upgrade-modal-close-button" disabled={Boolean(billingBusyKey)} onClick={() => setLockedFeatureKey("")} type="button">
                  Not now
                </button>
              </div>
                  </>
                );
              })()}
            </div>
          </div>
        ) : null}

        {!loading && activeTab === "blueprint" ? (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className={panelClass} data-testid="dashboard-blueprint-schema-card">
              <h2 className="text-3xl font-semibold text-[#001935]">Database Schema</h2>
              <div className="mt-6 grid gap-4">
                {Object.entries(summary.blueprint.database_schema || {}).map(([name, fields]) => (
                  <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`dashboard-schema-${name}`} key={name}>
                    <p className="text-lg font-semibold text-[#001935]">{name}</p>
                    <p className="mt-2 text-sm leading-7 text-[#42566f]">{fields.join(", ")}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={panelClass} data-testid="dashboard-blueprint-endpoints-card">
              <h2 className="text-3xl font-semibold text-[#001935]">API Endpoint List</h2>
              <div className="mt-6 grid gap-3">
                {(summary.blueprint.api_endpoints || []).map((endpoint) => (
                  <div className="rounded-[20px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-3 text-sm text-[#42566f]" data-testid={`dashboard-endpoint-${endpoint.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`} key={endpoint}>
                    {endpoint}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
        <AmbassadorClaimVerifyModal foundingMembership={summary.founding_membership} isSubmitting={claimVerifySubmitting} onClose={closeAmbassadorClaimModal} onPrimaryAction={startAmbassadorClaimVerify} open={showAmbassadorClaimModal} primaryLabel="Verify & Enter Private Showroom" previewHref={publicSiteHref} publicPath={publicSiteDisplay} user={user} />
        <FoundingMemberCelebration onClose={acknowledgeFoundingCelebration} open={showFoundingCelebration} user={user} />
        <SignatureWelcomeModal onClose={closeSignatureWelcomeModal} open={showSignatureWelcomeModal} user={user} />
        <AgentProfileSettingsSheet
          onProfileSaved={async (profileState) => {
            await onSessionRefresh?.();
            await loadDashboard();
            if (profileState?.dashboard_path && profileState.dashboard_path !== location.pathname) {
              navigate(profileState.dashboard_path, { replace: true });
            }
          }}
          open={showAgentProfileSettingsSheet}
          setOpen={setShowAgentProfileSettingsSheet}
          user={user}
        />
      </div>
    </div>
  );
};
